import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import helvetikerRegular from 'three/examples/fonts/helvetiker_regular.typeface.json';
import { MeshGradient } from '@paper-design/shaders-react';

import './viewModelPageStyles.css';

const FLOOR_MODEL_PATH = '/models/winter/floorpage3.glb';
const LOGO_MODEL_PATH = '/models/winter/logo.glb';
const HDR_TEXTURE_PATH = '/textures/mab/hdr.jpg';

const FLOOR_MODEL_CONFIG = {
  enabled: true,
  path: FLOOR_MODEL_PATH,
  autoCenter: false,
  position: new THREE.Vector3(0, 0, 0),
  scale: new THREE.Vector3(1, 1, 1),
  rotation: new THREE.Vector3(0, 0.4, 0),
  castShadow: true,
  receiveShadow: true
};

const CAMERA_CONFIG = {
  fov: 45,
  near: 0.01,
  far: 1000,
  position: new THREE.Vector3(20, 6, 50),
  target: new THREE.Vector3(0, 0, 0)
};

const CAMERA_CONTROL_CONFIG = {
  horizontalLimitDeg: 20,
  verticalLockPaddingDeg: 0.05,
  zoomEnabled: true,
  zoomStepCount: 2,
  zoomStepDistance: 22,
  startZoomStep: 2,
  startDistanceOffset: 0,
  minDistance: 1,
  zoomLerp: 0.085,
  zoomBackAngleLerp: 0.075,
  wheelCooldownMs: 140,
  rotateSpeed: 0.32,
  enableDamping: true,
  dampingFactor: 0.06,
  enablePan: false
};

const ZOOM_HINT_CONFIG = {
  enabled: true,
  text: 'Zoom in',
  left: '50%',
  top: '50%',
  x: 0,
  y: 350,
  z: 0,
  scale: 1,
  lightWidth: 110,
  duration: 3,
  lightColor: '#fafafa',
  backgroundColor: '#001031',
  backgroundOpacity: 1,
  borderColor: 'rgba(255, 255, 255, 0.0)',
  borderWidth: 2,
  lineColor: 'rgba(255, 255, 255, 0.92)',
  baseLineColor: 'rgba(255, 255, 255, 0.18)',
  glowOpacity: 1,
  glowLength: 13,
  glowBlur: 3,
  lineOpacity: 0.78,
  lineWidth: 1.25,
  lineRotationSeconds: 4.5,
  paddingX: 16,
  paddingY: 8,
  borderRadius: 999,
  opacity: 0.88,
  fontSize: 14,
  letterSpacing: 0,
  fadeSeconds: 0.55
};

const SCENE_TEXT_CONFIG = {
  enabled: true,
  visibleAfterFirstScroll: true,

  // Text content.
  title: 'ICEBERG INVESTMENTS',
  subtitle: 'High-Tech Saving and Investments Solutions',

  // Main transform controls for fixing the text inside the 3D scene.
  position: new THREE.Vector3(0, 2, 0),
  rotationXDeg: 0,
  rotationYDeg: 23,
  rotationZDeg: 0,
  scale: 0.8,

  // Text size/layout controls.
  titleSize: 1,
  subtitleSize: 0.52,
  subtitleFitWidthRatio: 0.82,
  letterSpacing: 0.035,
  spaceWidthRatio: 0.38,
  lineGap: 0.28,
  depth: 0.055,
  curveSegments: 12,
  bevelEnabled: true,
  bevelThickness: 0.012,
  bevelSize: 0.008,
  bevelSegments: 2,

  // Material controls.
  color: '#ffffff',
  emissiveColor: '#009ace',
  emissiveIntensity: 1.85,
  metalness: 0.12,
  roughness: 0.28,
  opacity: 0.94,

  // Scene behavior.
  castShadow: false,
  receiveShadow: false,
  fadeSeconds: 0.55
};

const SCENE_TEXT_HOVER_CONFIG = {
  enabled: true,

  // Only the real 3D letter geometry is raycasted. Empty space around the text will not trigger this.
  activeEmissiveIntensity: SCENE_TEXT_CONFIG.emissiveIntensity,
  inactiveEmissiveIntensity: 0,

  // Emissive hover timing. Change these values to control how fast each letter turns on/off.
  emissiveFadeInSeconds: 0.08,
  emissiveFadeOutSeconds: 1.15,

  // Glow that appears behind each individual letter while emissive is active.
  glowEnabled: true,
  glowColor: SCENE_TEXT_CONFIG.emissiveColor,
  glowOpacity: 0.72,
  glowWidthMultiplier: 6.35,
  glowHeightMultiplier: 6.15,
  glowMinWidth: 0.38,
  glowMinHeight: 0.38,
  glowZOffset: 0.035,
  glowTextureSize: 192,
  glowDepthTest: false,
  glowRenderOrder: 12,

  // When true, the mouse cursor changes only while it is exactly over the 3D letters.
  cursorPointerOnLetters: true
};

const SCENE_BUTTON_CONFIG = {
  enabled: true,

  // Button text.
  label: 'More',

  // Main transform controls for fixing the button inside the 3D scene.
  position: new THREE.Vector3(1.7, -0.3, 4),
  rotationXDeg: 0,
  rotationYDeg: 23,
  rotationZDeg: 0,
  scale: 0.8,

  // Button body controls.
  width: 0.8,
  height: 0.35,
  depth: 0.00,
  radius: 0.18,
  curveSegments: 16,
  bevelEnabled: true,
  bevelThickness: 0.012,
  bevelSize: 0.008,
  bevelSegments: 2,

  // Label controls.
  labelSize: 0.26,
  labelDepth: 0.025,
  labelFitWidthRatio: 0.5,
  labelYOffset: 0.02,
  labelZOffset: 0.05,

  // Material controls.
  backgroundColor: '#002d6d',
  backgroundEmissiveColor: '#002438',
  backgroundEmissiveIntensity: 0.18,
  backgroundOpacity: 0.58,
  borderColor: '#ffffff',
  borderOpacity: 0.48,
  labelColor: '#ffffff',
  labelEmissiveColor: '#009ace',
  labelEmissiveIntensity: 0.42,
  metalness: 0.14,
  roughness: 0.34,

  // Hover / glow controls.
  hoverEnabled: true,
  hoverFadeInSeconds: 0.08,
  hoverFadeOutSeconds: 0.75,
  hoverBackgroundEmissiveIntensity: 1.05,
  hoverLabelEmissiveIntensity: 1.35,
  hoverBorderOpacity: 0.92,
  glowEnabled: true,
  glowColor: '#009ace',
  glowOpacity: 0.68,
  glowWidthMultiplier: 2.35,
  glowHeightMultiplier: 2.7,
  glowZOffset: 0.035,
  glowDepthTest: false,
  glowRenderOrder: 13,
  cursorPointerOnButton: true,

  // Click controls.
  clickEnabled: true,
  clickUrl: 'https://iceberg.financial/investments/',
  openInNewTab: false,

  // Scene behavior.
  castShadow: false,
  receiveShadow: false
};

const NIGHT_ENVIRONMENT_CONFIG = {
  environmentIntensity: 1.15,
  toneMappingExposure: 0.85
};

const PAPER_BACKGROUND_CONFIG = {
  enabled: true,
  activeEffect: 'mesh',
  color1: '#01030d',
  color2: '#002b42',
  color3: '#01030d',
  color4: '#0002b42',
  speed: 0.75,
  backgroundColor: '#01030d',
  x: 0,
  y: 1,
  z: 1,
  scale: 1.14
};

const PAGE_LOADER_CONFIG = {
  enabled: true,
  minVisibleMs: 450,
  fadeSeconds: 0.65
};

const HORIZON_FOG_CONFIG = {
  enabled: false,
  type: 'exp2', // linear, exp2
  color: 0x0b2d3c,
  near: 7,
  far: 22,
  density: 0.075
};

const LIGHT_GUIDE_CONFIG = {
  enabled: false,
  size: 2.18,
  opacity: 0.92
};

const POINT_LIGHT_1_CONFIG = {
  color: '#ffffff',
  intensity: 30,
  distance: 5,
  decay: 1,
  position: new THREE.Vector3(-0.06, .5, -0.2),
  castShadow: true,
  shadowMapSize: 2048,
  shadowNear: 0.1,
  shadowFar: 40,
  shadowBias: -0.0004
};

const POINT_LIGHT_2_CONFIG = {
  color: 0xffffff,
  intensity: 500,
  distance: 500,
  decay: 2,
  position: new THREE.Vector3(-160, 0, -400),
  castShadow: true,
  shadowMapSize: 2048,
  shadowNear: 0.1,
  shadowFar: 80,
  shadowBias: -0.0004
};

const POINT_LIGHT_2_1_CONFIG = {
  color: 0xffffff,
  intensity: 500,
  distance: 500,
  decay: 2,
  position: new THREE.Vector3(-160, 20, -400),
  castShadow: true,
  shadowMapSize: 2048,
  shadowNear: 0.1,
  shadowFar: 80,
  shadowBias: -0.0004
};

const POINT_LIGHT_2_2_CONFIG = {
  color: 0xffffff,
  intensity: 500,
  distance: 500,
  decay: 2,
  position: new THREE.Vector3(-160, 50, -400),
  castShadow: true,
  shadowMapSize: 2048,
  shadowNear: 0.1,
  shadowFar: 80,
  shadowBias: -0.0004
};

const POINT_LIGHT_2_3_CONFIG = {
  color: 0xffffff,
  intensity: 500,
  distance: 500,
  decay: 2,
  position: new THREE.Vector3(-160, 70, -400),
  castShadow: true,
  shadowMapSize: 2048,
  shadowNear: 0.1,
  shadowFar: 80,
  shadowBias: -0.0004
};

const POINT_LIGHT_2_4_CONFIG = {
  color: 0xffffff,
  intensity: 500,
  distance: 500,
  decay: 2,
  position: new THREE.Vector3(-160, 90, -400),
  castShadow: true,
  shadowMapSize: 2048,
  shadowNear: 0.1,
  shadowFar: 80,
  shadowBias: -0.0004
};

const POINT_LIGHT_3_CONFIG = {
  color: 0xffffff,
  intensity: 10,
  distance: 5,
  decay: 2,
  position: new THREE.Vector3(0, 2, 0),
  castShadow: false,
  shadowMapSize: 2048,
  shadowNear: 0.1,
  shadowFar: 80,
  shadowBias: -0.0004
};

const POINT_LIGHT_4_CONFIG = {
  color: '#ffffff',
  intensity: 18,
  distance: 8,
  decay: 2,
  position: new THREE.Vector3(2, -6, 5),
  castShadow: false,
  shadowMapSize: 2048,
  shadowNear: 0.1,
  shadowFar: 80,
  shadowBias: -0.0004
};

const BACKGROUND_PARTICLES_CONFIG = {
  enabled: true,
  shape: 'sphere',
  followCameraPosition: true,
  sphereCoverageDeg: 360, // 360 or 180
  sphereRadius: 42,
  verticalMinDeg: -48,
  verticalMaxDeg: 48,
  center: new THREE.Vector3(0, 0, 0),
  count: 1000,
  areaWidth: 150,
  areaHeight: 150,
  areaDepth: 24,
  color: '#9feeff',
  opacity: 0.82,
  opacityRandomMin: 0.45,
  opacityRandomMax: 1,
  twinkleEnabled: true,
  twinkleChance: 0.1,
  twinkleIntensity: 2,
  twinkleSpeedMin: 0.35,
  twinkleSpeedMax: 3.15,
  twinkleSharpness: 8,
  pointSize: 0.55,
  pointSizeDistanceScale: 220,
  movementSpeed: 0.58,
  speedMin: 0.55,
  speedMax: 3.35,
  driftMin: 0.45,
  driftMax: 1.1,
  driftStrength: 0.42,
  yWaveSpeed: 0.72,
  yDriftScale: 0.65,
  zWaveSpeed: 0.48,
  zDriftScale: 0.45,
  alphaEdgeStart: 0.5,
  alphaEdgeEnd: 0,
  alphaDiscard: 0.01,
  depthTest: true,
  depthWrite: false,
  additiveBlending: true,
  renderOrder: 1
};


const INNER_PARTICLES_CONFIG = {
  enabled: true,

  // This is a completely separate particle system from BACKGROUND_PARTICLES_CONFIG.
  // Use it for the empty inside volume of the imaginary sphere.
  followCameraPosition: true,
  sphereRadius: 14,
  innerRadiusMin: 0.8,
  center: new THREE.Vector3(0, 0, 0),
  count: 1000,

  // Visual controls.
  color: '#b9f6ff',
  opacity: 0.58,
  opacityRandomMin: 0.18,
  opacityRandomMax: 1,
  pointSize: 0.12,
  pointSizeDistanceScale: 210,
  glowIntensity: 1.45,
  alphaEdgeStart: 0.5,
  alphaEdgeEnd: 0,
  alphaDiscard: 0.01,

  // Random motion controls.
  movementSpeed: 0.46,
  speedMin: 0.35,
  speedMax: 2.4,
  driftMin: 0.45,
  driftMax: 1.2,
  driftStrength: 0.34,
  yWaveSpeed: 0.7,
  yDriftScale: 0.62,
  zWaveSpeed: 0.52,
  zDriftScale: 0.5,

  // Twinkle / blinking controls.
  twinkleEnabled: true,
  twinkleChance: 0.16,
  twinkleIntensity: 2.35,
  twinkleSpeedMin: 0.45,
  twinkleSpeedMax: 3.8,
  twinkleSharpness: 8,

  // Hidden mouse soft-field controls.
  // This is no longer a hard push / explosion effect. It creates a slow, soft flow around the cursor.
  mouseEnabled: true,
  mouseRadius: 0.46, // Screen-space radius. Higher value affects a wider soft area around the cursor.
  mouseSoftness: 1.0,
  mouseFlowStrength: 3,
  mouseSwirlStrength: 0.36,
  mouseBreathStrength: 0.16,
  mouseDepthScale: 0.14,
  mouseZDrift: 0.45,
  mouseNoiseStrength: 0.08,
  mousePulseSpeed: 1.85,

  // Main field smoothing. Lower values make the invisible mouse energy move more slowly and softly.
  mouseFollowSpeed: 1.25,
  mouseFadeInSpeed: 1.45,
  mouseFadeOutSpeed: 0.32,

  // Soft residual field. This keeps a gentle after-flow behind the cursor without pushing particles hard.
  mouseTrailEnabled: true,
  mouseTrailFollowSpeed: 0.38,
  mouseTrailInfluence: 0.48,
  mouseTrailRadiusMultiplier: 1.38,
  mouseTrailFlowMultiplier: 0.36,

  // Render controls.
  depthTest: true,
  depthWrite: false,
  additiveBlending: true,
  renderOrder: 2
};

const LOGO_CONFIG = {
  enabled: true,
  path: LOGO_MODEL_PATH,
  position: new THREE.Vector3(-198, -150, -500),
  scale: 250,
  rotation: new THREE.Vector3(0, 0, 0),
  rotationEnabled: true,
  rotationAxis: 'y',
  rotationDurationSeconds: 10,
  rotationDirection: 1,
  castShadow: true,
  receiveShadow: true
};

const StarBackground = ({ color = 'currentColor', opacity = 1 }) => (
  <svg
    className="viewModelPage__star-button-bg-svg"
    width="100%"
    height="100%"
    preserveAspectRatio="none"
    viewBox="0 0 100 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <g clipPath="url(#viewModelPage-star-button-clip)">
      <path
        d="M32.34 26.68C32.34 26.3152 32.0445 26.02 31.68 26.02C31.3155 26.02 31.02 26.3152 31.02 26.68C31.02 27.0448 31.3155 27.34 31.68 27.34C32.0445 27.34 32.34 27.0448 32.34 26.68Z"
        fill="black"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M56.1 3.96C56.4645 3.96 56.76 4.25519 56.76 4.62C56.76 4.98481 56.4645 5.28 56.1 5.28C55.9131 5.28 55.7443 5.20201 55.624 5.07762C55.5632 5.01446 55.5147 4.93904 55.4829 4.8559C55.4552 4.78243 55.44 4.70315 55.44 4.62C55.44 4.5549 55.4494 4.49174 55.4668 4.43244C55.4906 4.35188 55.5292 4.27775 55.5795 4.21329C55.7004 4.05926 55.8885 3.96 56.1 3.96ZM40.26 17.16C40.6245 17.16 40.92 17.4552 40.92 17.82C40.92 18.1848 40.6245 18.48 40.26 18.48C39.8955 18.48 39.6 18.1848 39.6 17.82C39.6 17.4552 39.8955 17.16 40.26 17.16ZM74.58 5.28C74.7701 5.28 74.9413 5.36057 75.0618 5.48882C75.073 5.50043 75.0837 5.51268 75.094 5.52557C75.1088 5.54426 75.1231 5.56359 75.1359 5.58357L75.1479 5.60291L75.1595 5.62353C75.1711 5.64481 75.1814 5.66672 75.1906 5.68928C75.2226 5.76662 75.24 5.85106 75.24 5.94C75.24 6.1585 75.1336 6.3525 74.9699 6.47238C74.9158 6.51234 74.8555 6.54393 74.7908 6.56584C74.7247 6.58775 74.6538 6.6 74.58 6.6C74.2156 6.6 73.92 6.30481 73.92 5.94C73.92 5.87684 73.929 5.8156 73.9455 5.7576C73.9596 5.70862 73.979 5.66221 74.0032 5.61903C74.0657 5.50688 74.1595 5.41471 74.2728 5.35541C74.3647 5.30707 74.4691 5.28 74.58 5.28ZM21.66 33.52C22.0245 33.52 22.32 33.8152 22.32 34.18C22.32 34.5448 22.0245 34.84 21.66 34.84C21.2955 34.84 21 34.5448 21 34.18C21 33.8152 21.2955 33.52 21.66 33.52ZM8.16 32.86C8.16 32.4952 7.8645 32.2 7.5 32.2C7.1355 32.2 6.84 32.4952 6.84 32.86C6.84 33.2248 7.1355 33.52 7.5 33.52C7.8645 33.52 8.16 33.2248 8.16 32.86ZM7.5 23.68C7.8645 23.68 8.16 23.9752 8.16 24.34C8.16 24.7048 7.8645 25 7.5 25C7.1355 25 6.84 24.7048 6.84 24.34C6.84 23.9752 7.1355 23.68 7.5 23.68ZM19.32 18.48C19.32 18.1152 19.0245 17.82 18.66 17.82C18.2955 17.82 18 18.1152 18 18.48C18 18.8448 18.2955 19.14 18.66 19.14C19.0245 19.14 19.32 18.8448 19.32 18.48ZM5.66 11.84C6.0245 11.84 6.32001 12.1352 6.32001 12.5C6.32001 12.8648 6.0245 13.16 5.66 13.16C5.2955 13.16 5 12.8648 5 12.5C5 12.1352 5.2955 11.84 5.66 11.84ZM35.16 35.5C35.16 35.1355 34.8645 34.84 34.5 34.84C34.1355 34.84 33.84 35.1355 33.84 35.5C33.84 35.8645 34.1355 36.16 34.5 36.16C34.8645 36.16 35.16 35.8645 35.16 35.5ZM53.5 36.18C53.8645 36.18 54.16 36.4752 54.16 36.84C54.16 37.2048 53.8645 37.5 53.5 37.5C53.1355 37.5 52.84 37.2048 52.84 36.84C52.84 36.4752 53.1355 36.18 53.5 36.18ZM48.5 28.66C48.5 28.2952 48.2045 28 47.84 28C47.4755 28 47.18 28.2952 47.18 28.66C47.18 29.0248 47.4755 29.32 47.84 29.32C48.2045 29.32 48.5 29.0248 48.5 28.66ZM60.34 27.34C60.7045 27.34 61 27.6352 61 28C61 28.3648 60.7045 28.66 60.34 28.66C59.9755 28.66 59.68 28.3648 59.68 28C59.68 27.6352 59.9755 27.34 60.34 27.34ZM56.284 16.5C56.284 16.1352 55.9885 15.84 55.624 15.84C55.2595 15.84 54.964 16.1352 54.964 16.5C54.964 16.8648 55.2595 17.16 55.624 17.16C55.9885 17.16 56.284 16.8648 56.284 16.5ZM46.2 7.26C46.2 6.89519 45.9045 6.6 45.54 6.6C45.5174 6.6 45.4953 6.60129 45.4733 6.60387L45.453 6.60579L45.4124 6.61225L45.3857 6.61804L45.3845 6.61836C45.3675 6.62277 45.3504 6.62721 45.3341 6.63287C45.2522 6.65929 45.1774 6.70184 45.1134 6.75597C45.0627 6.79916 45.0186 6.84943 44.9828 6.90551C44.9178 7.00799 44.88 7.12981 44.88 7.26C44.88 7.62481 45.1755 7.92 45.54 7.92C45.7372 7.92 45.9141 7.83363 46.0353 7.69635C46.0808 7.64478 46.1182 7.58613 46.1459 7.52232C46.1807 7.4424 46.2 7.35346 46.2 7.26ZM33 9.34C33 8.9752 32.7045 8.68 32.34 8.68C31.9755 8.68 31.68 8.9752 31.68 9.34C31.68 9.7048 31.9755 10 32.34 10C32.7045 10 33 9.7048 33 9.34ZM16 4.8559C16.3645 4.8559 16.66 5.1511 16.66 5.5159C16.66 5.8807 16.3645 6.1759 16 6.1759C15.6355 6.1759 15.34 5.8807 15.34 5.5159C15.34 5.1511 15.6355 4.8559 16 4.8559ZM69.66 21.16C69.66 20.7952 69.3645 20.5 69 20.5C68.6355 20.5 68.34 20.7952 68.34 21.16C68.34 21.5248 68.6355 21.82 69 21.82C69.3645 21.82 69.66 21.5248 69.66 21.16ZM80.52 15.18C80.52 14.8152 80.2245 14.52 79.86 14.52C79.4956 14.52 79.2 14.8152 79.2 15.18C79.2 15.5448 79.4956 15.84 79.86 15.84C80.2245 15.84 80.52 15.5448 80.52 15.18ZM78.16 34.84C78.16 34.4752 77.5 34.18 77.5 34.18C77.5 34.18 76.84 34.4752 76.84 34.84C76.84 35.2048 77.1355 35.5 77.5 35.5C77.8645 35.5 78.16 35.2048 78.16 34.84ZM85.66 24.34C86.0245 24.34 86.32 24.6352 86.32 25C86.32 25.3648 86.0245 25.66 85.66 25.66C85.2955 25.66 85 25.3648 85 25C85 24.6352 85.2955 24.34 85.66 24.34ZM91.32 10C91.32 9.6352 91.0245 9.34 90.66 9.34C90.2955 9.34 90 9.6352 90 10C90 10.3648 90.2955 10.66 90.66 10.66C91.0245 10.66 91.32 10.3648 91.32 10ZM138.6 0H0V46.2H138.6V0ZM92.64 34.84C92.64 34.4752 91.98 34.18 91.98 34.18C91.98 34.18 91.32 34.4752 91.32 34.84C91.32 35.2048 91.6155 35.5 91.98 35.5C92.3445 35.5 92.64 35.2048 92.64 34.84Z"
        fill={color}
        fillOpacity={opacity}
      />
    </g>
    <defs>
      <clipPath id="viewModelPage-star-button-clip">
        <rect width="100" height="40" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const StarZoomHint = ({ visible }) => {
  const buttonRef = useRef(null);

  useEffect(() => {
    const updatePath = () => {
      if (!buttonRef.current) return;
      const { offsetWidth, offsetHeight } = buttonRef.current;
      buttonRef.current.style.setProperty(
        '--star-button-path',
        `path('M 0 0 H ${offsetWidth} V ${offsetHeight} H 0 V 0')`
      );
    };

    updatePath();
    window.addEventListener('resize', updatePath);
    return () => window.removeEventListener('resize', updatePath);
  }, []);

  return (
    <div
      ref={buttonRef}
      className={`viewModelPage__zoom-hint viewModelPage__star-button ${visible ? 'viewModelPage__zoom-hint--visible' : 'viewModelPage__zoom-hint--hidden'}`}
      style={{
        left: ZOOM_HINT_CONFIG.left,
        top: ZOOM_HINT_CONFIG.top,
        opacity: visible ? ZOOM_HINT_CONFIG.opacity : 0,
        fontSize: `${ZOOM_HINT_CONFIG.fontSize}px`,
        letterSpacing: `${ZOOM_HINT_CONFIG.letterSpacing}px`,
        transitionDuration: `${ZOOM_HINT_CONFIG.fadeSeconds}s`,
        '--star-button-duration': `${ZOOM_HINT_CONFIG.duration}s`,
        '--star-button-light-width': `${ZOOM_HINT_CONFIG.lightWidth}px`,
        '--star-button-light-color': ZOOM_HINT_CONFIG.lightColor,
        '--star-button-background-color': ZOOM_HINT_CONFIG.backgroundColor,
        '--star-button-border-color': ZOOM_HINT_CONFIG.borderColor,
        '--star-button-border-width': `${ZOOM_HINT_CONFIG.borderWidth}px`,
        '--star-button-padding-x': `${ZOOM_HINT_CONFIG.paddingX}px`,
        '--star-button-padding-y': `${ZOOM_HINT_CONFIG.paddingY}px`,
        '--star-button-radius': `${ZOOM_HINT_CONFIG.borderRadius}px`,
        '--zoom-hint-line-color': ZOOM_HINT_CONFIG.lineColor,
        '--zoom-hint-base-line-color': ZOOM_HINT_CONFIG.baseLineColor,
        '--zoom-hint-glow-opacity': ZOOM_HINT_CONFIG.glowOpacity,
        '--zoom-hint-glow-blur': `${ZOOM_HINT_CONFIG.glowBlur}px`,
        '--zoom-hint-line-opacity': ZOOM_HINT_CONFIG.lineOpacity,
        '--zoom-hint-line-width': `${ZOOM_HINT_CONFIG.lineWidth}px`,
        '--zoom-hint-line-speed': `${ZOOM_HINT_CONFIG.lineRotationSeconds}s`,
        '--zoom-hint-x': `${ZOOM_HINT_CONFIG.x}px`,
        '--zoom-hint-y': `${ZOOM_HINT_CONFIG.y}px`,
        '--zoom-hint-z': `${ZOOM_HINT_CONFIG.z}px`,
        '--zoom-hint-scale': ZOOM_HINT_CONFIG.scale
      }}
      aria-hidden="true"
    >
      <div className="viewModelPage__star-button-light" />
      <div className="viewModelPage__star-button-stars">
        <StarBackground
          color="var(--star-button-background-color)"
          opacity={ZOOM_HINT_CONFIG.backgroundOpacity}
        />
      </div>
      <span className="viewModelPage__star-button-label">{ZOOM_HINT_CONFIG.text}</span>
    </div>
  );
};


const ViewModelPage = () => {
  const mountRef = useRef(null);
  const zoomHintDismissedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [zoomHintVisible, setZoomHintVisible] = useState(ZOOM_HINT_CONFIG.enabled);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    let isMounted = true;
    let animationFrameId = 0;
    let pageLoaderTimeoutId = 0;
    const pageLoaderStartedAt = performance.now();
    const loadingManager = new THREE.LoadingManager();
    const finishPageLoading = () => {
      if (!isMounted) return;
      const elapsed = performance.now() - pageLoaderStartedAt;
      const delay = PAGE_LOADER_CONFIG.enabled
        ? Math.max(0, PAGE_LOADER_CONFIG.minVisibleMs - elapsed)
        : 0;

      window.clearTimeout(pageLoaderTimeoutId);
      pageLoaderTimeoutId = window.setTimeout(() => {
        if (!isMounted) return;
        window.requestAnimationFrame(() => {
          if (!isMounted) return;
          window.requestAnimationFrame(() => {
            if (isMounted) setLoading(false);
          });
        });
      }, delay);
    };
    loadingManager.onLoad = finishPageLoading;
    loadingManager.onError = (url) => {
      console.error('Error loading viewModelPage asset:', url);
    };
    const rotatingLogos = [];
    const clock = new THREE.Clock();
    const sceneRevealMaterials = [];
    const sceneTextHoverLetters = [];
    const sceneTextHoverMeshes = [];
    const sceneButtonHoverItems = [];
    const sceneButtonHoverMeshes = [];
    const sceneTextRaycaster = new THREE.Raycaster();
    const sceneTextPointer = new THREE.Vector2(999, 999);
    let sceneTextPointerInside = false;
    const innerParticleMouseTarget = new THREE.Vector2(999, 999);
    const innerParticleMouseCurrent = new THREE.Vector2(999, 999);
    const innerParticleMouseTrail = new THREE.Vector2(999, 999);
    let innerParticleMouseStrength = 0;
    let innerParticleMouseTrailStrength = 0;
    let innerParticleMouseTargetStrength = 0;
    let sceneTextGroup = null;
    let sceneButtonGroup = null;
    let sceneTextOpacity = SCENE_TEXT_CONFIG.visibleAfterFirstScroll ? 0 : 1;
    let sceneTextTargetOpacity = SCENE_TEXT_CONFIG.visibleAfterFirstScroll ? 0 : 1;
    let revealZoomHintAfterSceneHide = false;

    const setSceneTextOpacity = (opacityValue) => {
      sceneRevealMaterials.forEach((material) => {
        const finalOpacity = material.userData.finalOpacity ?? 1;
        material.opacity = finalOpacity * opacityValue;
        material.transparent = material.opacity < 1 || material.userData.forceTransparent === true;
        material.needsUpdate = true;
      });
    };

    const revealSceneText = () => {
      if (!SCENE_TEXT_CONFIG.enabled && !SCENE_BUTTON_CONFIG.enabled) return;
      revealZoomHintAfterSceneHide = false;
      sceneTextTargetOpacity = 1;
      if (ZOOM_HINT_CONFIG.enabled) setZoomHintVisible(false);
      if (sceneTextGroup) sceneTextGroup.visible = true;
      if (sceneButtonGroup) sceneButtonGroup.visible = true;
    };

    const hideSceneText = ({ revealZoomHintAfterFade = false } = {}) => {
      if (!SCENE_TEXT_CONFIG.enabled && !SCENE_BUTTON_CONFIG.enabled) return;
      sceneTextTargetOpacity = 0;
      revealZoomHintAfterSceneHide = revealZoomHintAfterFade;
    };

    const scene = new THREE.Scene();
    scene.background = null;
    if (HORIZON_FOG_CONFIG.enabled) {
      scene.fog = HORIZON_FOG_CONFIG.type === 'exp2'
        ? new THREE.FogExp2(HORIZON_FOG_CONFIG.color, HORIZON_FOG_CONFIG.density)
        : new THREE.Fog(HORIZON_FOG_CONFIG.color, HORIZON_FOG_CONFIG.near, HORIZON_FOG_CONFIG.far);
    }

    const camera = new THREE.PerspectiveCamera(
      CAMERA_CONFIG.fov,
      mount.clientWidth / Math.max(1, mount.clientHeight),
      CAMERA_CONFIG.near,
      CAMERA_CONFIG.far
    );
    const baseCameraOffset = CAMERA_CONFIG.position.clone().sub(CAMERA_CONFIG.target);
    const baseCameraDistance = baseCameraOffset.length();
    const maxZoomStep = Math.max(0, CAMERA_CONTROL_CONFIG.zoomStepCount);
    const initialZoomStep = THREE.MathUtils.clamp(CAMERA_CONTROL_CONFIG.startZoomStep, 0, maxZoomStep);
    const minCameraDistance = Math.max(CAMERA_CONFIG.near + 0.001, CAMERA_CONTROL_CONFIG.minDistance);
    const startCameraDistance = Math.max(minCameraDistance, baseCameraDistance + CAMERA_CONTROL_CONFIG.startDistanceOffset);
    const getCameraDistanceForZoomStep = (zoomStep) => Math.max(
      minCameraDistance,
      startCameraDistance + (zoomStep - initialZoomStep) * CAMERA_CONTROL_CONFIG.zoomStepDistance
    );
    let targetZoomStep = initialZoomStep;
    let currentCameraDistance = startCameraDistance;
    let targetCameraDistance = currentCameraDistance;
    const cameraDirection = baseCameraOffset.clone().normalize();
    camera.position.copy(CAMERA_CONFIG.target).addScaledVector(cameraDirection, currentCameraDistance);
    camera.lookAt(CAMERA_CONFIG.target);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      premultipliedAlpha: false,
      powerPreference: 'high-performance',

      failIfMajorPerformanceCaveat: false,
      preserveDrawingBuffer: false,
    });
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = NIGHT_ENVIRONMENT_CONFIG.toneMappingExposure;
    renderer.shadowMap.enabled = false;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mount.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.copy(CAMERA_CONFIG.target);
    controls.enableDamping = CAMERA_CONTROL_CONFIG.enableDamping;
    controls.dampingFactor = CAMERA_CONTROL_CONFIG.dampingFactor;
    controls.rotateSpeed = CAMERA_CONTROL_CONFIG.rotateSpeed;
    controls.enablePan = CAMERA_CONTROL_CONFIG.enablePan;
    controls.enableZoom = false;
    controls.enableRotate = initialZoomStep === 0;

    const baseSpherical = new THREE.Spherical().setFromVector3(baseCameraOffset);
    const liveCameraOffset = new THREE.Vector3();
    const liveCameraSpherical = new THREE.Spherical();
    const horizontalLimit = THREE.MathUtils.degToRad(CAMERA_CONTROL_CONFIG.horizontalLimitDeg);
    const verticalPadding = THREE.MathUtils.degToRad(CAMERA_CONTROL_CONFIG.verticalLockPaddingDeg);
    controls.minAzimuthAngle = baseSpherical.theta - horizontalLimit;
    controls.maxAzimuthAngle = baseSpherical.theta + horizontalLimit;
    controls.minPolarAngle = Math.max(0.001, baseSpherical.phi - verticalPadding);
    controls.maxPolarAngle = Math.min(Math.PI - 0.001, baseSpherical.phi + verticalPadding);
    controls.minDistance = minCameraDistance;
    controls.maxDistance = Math.max(
      startCameraDistance,
      getCameraDistanceForZoomStep(0),
      getCameraDistanceForZoomStep(maxZoomStep)
    );
    controls.update();
    let lastWheelTime = 0;
    const handleCameraWheel = (event) => {
      if (!CAMERA_CONTROL_CONFIG.zoomEnabled) return;
      event.preventDefault();

      const now = performance.now();
      if (now - lastWheelTime < CAMERA_CONTROL_CONFIG.wheelCooldownMs) return;
      lastWheelTime = now;

      if (event.deltaY < 0) {
        const nextZoomStep = Math.max(0, targetZoomStep - 1);
        if (nextZoomStep !== targetZoomStep) {
          zoomHintDismissedRef.current = true;
          revealSceneText();
        }
        targetZoomStep = nextZoomStep;
      } else if (event.deltaY > 0) {
        const nextZoomStep = Math.min(maxZoomStep, targetZoomStep + 1);
        if (nextZoomStep !== targetZoomStep && nextZoomStep === maxZoomStep) {
          hideSceneText({ revealZoomHintAfterFade: true });
        }
        targetZoomStep = nextZoomStep;
        controls.enableRotate = false;
      }

      targetCameraDistance = getCameraDistanceForZoomStep(targetZoomStep);
    };
    renderer.domElement.addEventListener('wheel', handleCameraWheel, { passive: false });

    const handleScenePointerMove = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);
      sceneTextPointer.x = ((event.clientX - rect.left) / width) * 2 - 1;
      sceneTextPointer.y = -(((event.clientY - rect.top) / height) * 2 - 1);
      sceneTextPointerInside = true;

      if (INNER_PARTICLES_CONFIG.enabled && INNER_PARTICLES_CONFIG.mouseEnabled) {
        innerParticleMouseTarget.copy(sceneTextPointer);
        innerParticleMouseTargetStrength = 1;
      }
    };

    const handleScenePointerLeave = () => {
      sceneTextPointerInside = false;
      innerParticleMouseTargetStrength = 0;
      renderer.domElement.style.cursor = '';
    };

    const handleScenePointerDown = (event) => {
      if (!SCENE_BUTTON_CONFIG.enabled || !SCENE_BUTTON_CONFIG.clickEnabled || !SCENE_BUTTON_CONFIG.clickUrl) return;
      if (!sceneButtonGroup || !sceneButtonGroup.visible || sceneTextOpacity <= 0.03) return;

      const rect = renderer.domElement.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);
      const pointer = new THREE.Vector2(
        ((event.clientX - rect.left) / width) * 2 - 1,
        -(((event.clientY - rect.top) / height) * 2 - 1)
      );

      sceneTextRaycaster.setFromCamera(pointer, camera);
      const intersections = sceneTextRaycaster.intersectObjects(sceneButtonHoverMeshes, false);
      if (intersections.length === 0) return;

      if (SCENE_BUTTON_CONFIG.openInNewTab) {
        window.open(SCENE_BUTTON_CONFIG.clickUrl, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = SCENE_BUTTON_CONFIG.clickUrl;
      }
    };

    renderer.domElement.addEventListener('pointermove', handleScenePointerMove);
    renderer.domElement.addEventListener('pointerleave', handleScenePointerLeave);
    renderer.domElement.addEventListener('pointerdown', handleScenePointerDown);

    const lightGuides = [];
    const createPointLightWithGuide = (config, name) => {
      const light = new THREE.PointLight(
        config.color,
        config.intensity,
        config.distance,
        config.decay
      );
      light.name = name;
      light.position.copy(config.position);
      light.castShadow = config.castShadow;
      light.shadow.mapSize.set(config.shadowMapSize, config.shadowMapSize);
      light.shadow.camera.near = config.shadowNear;
      light.shadow.camera.far = config.shadowFar;
      light.shadow.bias = config.shadowBias;
      scene.add(light);

      if (LIGHT_GUIDE_CONFIG.enabled) {
        const guideGeometry = new THREE.SphereGeometry(LIGHT_GUIDE_CONFIG.size, 24, 16);
        const guideMaterial = new THREE.MeshBasicMaterial({
          color: config.color,
          transparent: true,
          opacity: LIGHT_GUIDE_CONFIG.opacity
        });
        const guide = new THREE.Mesh(guideGeometry, guideMaterial);
        guide.name = `${name} Guide`;
        guide.position.copy(config.position);
        scene.add(guide);
        lightGuides.push(guide);
      }

      return light;
    };

    const createBackgroundParticles = () => {
      if (!BACKGROUND_PARTICLES_CONFIG.enabled) return null;

      const {
        count,
        center,
        shape,
        sphereCoverageDeg,
        sphereRadius,
        verticalMinDeg,
        verticalMaxDeg,
        areaWidth,
        areaHeight,
        areaDepth,
        speedMin,
        speedMax,
        driftMin,
        driftMax,
        opacityRandomMin,
        opacityRandomMax,
        twinkleChance,
        twinkleSpeedMin,
        twinkleSpeedMax
      } = BACKGROUND_PARTICLES_CONFIG;
      const positions = new Float32Array(count * 3);
      const phases = new Float32Array(count);
      const speeds = new Float32Array(count);
      const drifts = new Float32Array(count);
      const opacityRandoms = new Float32Array(count);
      const twinkleStrengths = new Float32Array(count);
      const twinkleSpeeds = new Float32Array(count);
      const twinklePhases = new Float32Array(count);
      const coverageRad = THREE.MathUtils.degToRad(sphereCoverageDeg);
      const verticalMinRad = THREE.MathUtils.degToRad(verticalMinDeg);
      const verticalMaxRad = THREE.MathUtils.degToRad(verticalMaxDeg);

      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        if (shape === 'sphere') {
          const azimuth = (Math.random() - 0.5) * coverageRad;
          const elevation = verticalMinRad + Math.random() * (verticalMaxRad - verticalMinRad);
          const horizontalRadius = Math.cos(elevation) * sphereRadius;

          positions[i3] = center.x + Math.sin(azimuth) * horizontalRadius;
          positions[i3 + 1] = center.y + Math.sin(elevation) * sphereRadius;
          positions[i3 + 2] = center.z - Math.cos(azimuth) * horizontalRadius;
        } else {
          positions[i3] = center.x + (Math.random() - 0.5) * areaWidth;
          positions[i3 + 1] = center.y + (Math.random() - 0.5) * areaHeight;
          positions[i3 + 2] = center.z + (Math.random() - 0.5) * areaDepth;
        }
        phases[i] = Math.random() * Math.PI * 2;
        speeds[i] = speedMin + Math.random() * (speedMax - speedMin);
        drifts[i] = driftMin + Math.random() * (driftMax - driftMin);
        opacityRandoms[i] = opacityRandomMin + Math.random() * (opacityRandomMax - opacityRandomMin);
        twinkleStrengths[i] = Math.random() < twinkleChance ? Math.random() : 0;
        twinkleSpeeds[i] = twinkleSpeedMin + Math.random() * (twinkleSpeedMax - twinkleSpeedMin);
        twinklePhases[i] = Math.random() * Math.PI * 2;
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));
      geometry.setAttribute('aSpeed', new THREE.BufferAttribute(speeds, 1));
      geometry.setAttribute('aDrift', new THREE.BufferAttribute(drifts, 1));
      geometry.setAttribute('aOpacityRandom', new THREE.BufferAttribute(opacityRandoms, 1));
      geometry.setAttribute('aTwinkleStrength', new THREE.BufferAttribute(twinkleStrengths, 1));
      geometry.setAttribute('aTwinkleSpeed', new THREE.BufferAttribute(twinkleSpeeds, 1));
      geometry.setAttribute('aTwinklePhase', new THREE.BufferAttribute(twinklePhases, 1));

      const material = new THREE.ShaderMaterial({
        transparent: true,
        depthTest: BACKGROUND_PARTICLES_CONFIG.depthTest,
        depthWrite: BACKGROUND_PARTICLES_CONFIG.depthWrite,
        blending: BACKGROUND_PARTICLES_CONFIG.additiveBlending ? THREE.AdditiveBlending : THREE.NormalBlending,
        uniforms: {
          uTime: { value: 0 },
          uColor: { value: new THREE.Color(BACKGROUND_PARTICLES_CONFIG.color) },
          uOpacity: { value: BACKGROUND_PARTICLES_CONFIG.opacity },
          uPointSize: { value: BACKGROUND_PARTICLES_CONFIG.pointSize },
          uPointSizeDistanceScale: { value: BACKGROUND_PARTICLES_CONFIG.pointSizeDistanceScale },
          uMovementSpeed: { value: BACKGROUND_PARTICLES_CONFIG.movementSpeed },
          uDriftStrength: { value: BACKGROUND_PARTICLES_CONFIG.driftStrength },
          uYWaveSpeed: { value: BACKGROUND_PARTICLES_CONFIG.yWaveSpeed },
          uYDriftScale: { value: BACKGROUND_PARTICLES_CONFIG.yDriftScale },
          uZWaveSpeed: { value: BACKGROUND_PARTICLES_CONFIG.zWaveSpeed },
          uZDriftScale: { value: BACKGROUND_PARTICLES_CONFIG.zDriftScale },
          uAlphaEdgeStart: { value: BACKGROUND_PARTICLES_CONFIG.alphaEdgeStart },
          uAlphaEdgeEnd: { value: BACKGROUND_PARTICLES_CONFIG.alphaEdgeEnd },
          uAlphaDiscard: { value: BACKGROUND_PARTICLES_CONFIG.alphaDiscard },
          uTwinkleEnabled: { value: BACKGROUND_PARTICLES_CONFIG.twinkleEnabled ? 1 : 0 },
          uTwinkleIntensity: { value: BACKGROUND_PARTICLES_CONFIG.twinkleIntensity },
          uTwinkleSharpness: { value: BACKGROUND_PARTICLES_CONFIG.twinkleSharpness }
        },
        vertexShader: `
          attribute float aPhase;
          attribute float aSpeed;
          attribute float aDrift;
          attribute float aOpacityRandom;
          attribute float aTwinkleStrength;
          attribute float aTwinkleSpeed;
          attribute float aTwinklePhase;
          uniform float uTime;
          uniform float uPointSize;
          uniform float uPointSizeDistanceScale;
          uniform float uMovementSpeed;
          uniform float uDriftStrength;
          uniform float uYWaveSpeed;
          uniform float uYDriftScale;
          uniform float uZWaveSpeed;
          uniform float uZDriftScale;
          uniform float uTwinkleEnabled;
          uniform float uTwinkleIntensity;
          uniform float uTwinkleSharpness;
          varying float vOpacityMultiplier;
          varying float vTwinkleGlow;

          void main() {
            vec3 pos = position;
            float t = uTime * uMovementSpeed * aSpeed + aPhase;
            pos.x += sin(t) * uDriftStrength * aDrift;
            pos.y += cos(t * uYWaveSpeed) * uDriftStrength * uYDriftScale * aDrift;
            pos.z += sin(t * uZWaveSpeed) * uDriftStrength * uZDriftScale * aDrift;
            float twinkleWave = sin(uTime * aTwinkleSpeed + aTwinklePhase) * 0.5 + 0.5;
            float twinkle = pow(twinkleWave, uTwinkleSharpness) * aTwinkleStrength * uTwinkleEnabled;
            vOpacityMultiplier = aOpacityRandom * (1.0 + twinkle * uTwinkleIntensity);
            vTwinkleGlow = twinkle * uTwinkleIntensity;

            vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
            gl_PointSize = uPointSize * (uPointSizeDistanceScale / max(1.0, -mvPosition.z));
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          uniform vec3 uColor;
          uniform float uOpacity;
          uniform float uAlphaEdgeStart;
          uniform float uAlphaEdgeEnd;
          uniform float uAlphaDiscard;
          varying float vOpacityMultiplier;
          varying float vTwinkleGlow;

          void main() {
            vec2 point = gl_PointCoord - vec2(0.5);
            float dist = length(point);
            float innerEdge = min(uAlphaEdgeStart, uAlphaEdgeEnd);
            float outerEdge = max(uAlphaEdgeStart, uAlphaEdgeEnd);
            float alpha = clamp((1.0 - smoothstep(innerEdge, outerEdge, dist)) * uOpacity * vOpacityMultiplier, 0.0, 1.0);
            if (alpha < uAlphaDiscard) discard;
            gl_FragColor = vec4(uColor * (1.0 + vTwinkleGlow), alpha);
          }
        `
      });

      const particles = new THREE.Points(geometry, material);
      particles.frustumCulled = false;
      particles.renderOrder = BACKGROUND_PARTICLES_CONFIG.renderOrder;
      return particles;
    };


    const createInnerSphereParticles = () => {
      if (!INNER_PARTICLES_CONFIG.enabled) return null;

      const {
        count,
        center,
        sphereRadius,
        innerRadiusMin,
        speedMin,
        speedMax,
        driftMin,
        driftMax,
        opacityRandomMin,
        opacityRandomMax,
        twinkleChance,
        twinkleSpeedMin,
        twinkleSpeedMax
      } = INNER_PARTICLES_CONFIG;

      const positions = new Float32Array(count * 3);
      const phases = new Float32Array(count);
      const speeds = new Float32Array(count);
      const drifts = new Float32Array(count);
      const opacityRandoms = new Float32Array(count);
      const twinkleStrengths = new Float32Array(count);
      const twinkleSpeeds = new Float32Array(count);
      const twinklePhases = new Float32Array(count);
      const safeInnerRadius = Math.max(0, Math.min(innerRadiusMin, sphereRadius));
      const innerRadiusCubed = safeInnerRadius * safeInnerRadius * safeInnerRadius;
      const radiusRangeCubed = Math.max(0.0001, sphereRadius * sphereRadius * sphereRadius - innerRadiusCubed);

      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        const u = Math.random();
        const v = Math.random();
        const theta = u * Math.PI * 2;
        const phi = Math.acos(2 * v - 1);
        const radius = Math.cbrt(innerRadiusCubed + Math.random() * radiusRangeCubed);
        const sinPhi = Math.sin(phi);

        positions[i3] = center.x + radius * sinPhi * Math.cos(theta);
        positions[i3 + 1] = center.y + radius * Math.cos(phi);
        positions[i3 + 2] = center.z + radius * sinPhi * Math.sin(theta);

        phases[i] = Math.random() * Math.PI * 2;
        speeds[i] = speedMin + Math.random() * (speedMax - speedMin);
        drifts[i] = driftMin + Math.random() * (driftMax - driftMin);
        opacityRandoms[i] = opacityRandomMin + Math.random() * (opacityRandomMax - opacityRandomMin);
        twinkleStrengths[i] = Math.random() < twinkleChance ? Math.random() : 0;
        twinkleSpeeds[i] = twinkleSpeedMin + Math.random() * (twinkleSpeedMax - twinkleSpeedMin);
        twinklePhases[i] = Math.random() * Math.PI * 2;
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));
      geometry.setAttribute('aSpeed', new THREE.BufferAttribute(speeds, 1));
      geometry.setAttribute('aDrift', new THREE.BufferAttribute(drifts, 1));
      geometry.setAttribute('aOpacityRandom', new THREE.BufferAttribute(opacityRandoms, 1));
      geometry.setAttribute('aTwinkleStrength', new THREE.BufferAttribute(twinkleStrengths, 1));
      geometry.setAttribute('aTwinkleSpeed', new THREE.BufferAttribute(twinkleSpeeds, 1));
      geometry.setAttribute('aTwinklePhase', new THREE.BufferAttribute(twinklePhases, 1));

      const material = new THREE.ShaderMaterial({
        transparent: true,
        depthTest: INNER_PARTICLES_CONFIG.depthTest,
        depthWrite: INNER_PARTICLES_CONFIG.depthWrite,
        blending: INNER_PARTICLES_CONFIG.additiveBlending ? THREE.AdditiveBlending : THREE.NormalBlending,
        uniforms: {
          uTime: { value: 0 },
          uColor: { value: new THREE.Color(INNER_PARTICLES_CONFIG.color) },
          uOpacity: { value: INNER_PARTICLES_CONFIG.opacity },
          uPointSize: { value: INNER_PARTICLES_CONFIG.pointSize },
          uPointSizeDistanceScale: { value: INNER_PARTICLES_CONFIG.pointSizeDistanceScale },
          uGlowIntensity: { value: INNER_PARTICLES_CONFIG.glowIntensity },
          uMovementSpeed: { value: INNER_PARTICLES_CONFIG.movementSpeed },
          uDriftStrength: { value: INNER_PARTICLES_CONFIG.driftStrength },
          uYWaveSpeed: { value: INNER_PARTICLES_CONFIG.yWaveSpeed },
          uYDriftScale: { value: INNER_PARTICLES_CONFIG.yDriftScale },
          uZWaveSpeed: { value: INNER_PARTICLES_CONFIG.zWaveSpeed },
          uZDriftScale: { value: INNER_PARTICLES_CONFIG.zDriftScale },
          uAlphaEdgeStart: { value: INNER_PARTICLES_CONFIG.alphaEdgeStart },
          uAlphaEdgeEnd: { value: INNER_PARTICLES_CONFIG.alphaEdgeEnd },
          uAlphaDiscard: { value: INNER_PARTICLES_CONFIG.alphaDiscard },
          uTwinkleEnabled: { value: INNER_PARTICLES_CONFIG.twinkleEnabled ? 1 : 0 },
          uTwinkleIntensity: { value: INNER_PARTICLES_CONFIG.twinkleIntensity },
          uTwinkleSharpness: { value: INNER_PARTICLES_CONFIG.twinkleSharpness },
          uMouseEnabled: { value: INNER_PARTICLES_CONFIG.mouseEnabled ? 1 : 0 },
          uMouseNdc: { value: innerParticleMouseCurrent.clone() },
          uMouseTrailNdc: { value: innerParticleMouseTrail.clone() },
          uMouseInfluence: { value: 0 },
          uMouseTrailInfluence: { value: 0 },
          uMouseRadius: { value: INNER_PARTICLES_CONFIG.mouseRadius },
          uMouseSoftness: { value: INNER_PARTICLES_CONFIG.mouseSoftness },
          uMouseFlowStrength: { value: INNER_PARTICLES_CONFIG.mouseFlowStrength },
          uMouseSwirlStrength: { value: INNER_PARTICLES_CONFIG.mouseSwirlStrength },
          uMouseBreathStrength: { value: INNER_PARTICLES_CONFIG.mouseBreathStrength },
          uMouseTrailRadiusMultiplier: { value: INNER_PARTICLES_CONFIG.mouseTrailRadiusMultiplier },
          uMouseTrailFlowMultiplier: { value: INNER_PARTICLES_CONFIG.mouseTrailFlowMultiplier },
          uMouseDepthScale: { value: INNER_PARTICLES_CONFIG.mouseDepthScale },
          uMouseZDrift: { value: INNER_PARTICLES_CONFIG.mouseZDrift },
          uMouseNoiseStrength: { value: INNER_PARTICLES_CONFIG.mouseNoiseStrength },
          uMousePulseSpeed: { value: INNER_PARTICLES_CONFIG.mousePulseSpeed }
        },
        vertexShader: `
          attribute float aPhase;
          attribute float aSpeed;
          attribute float aDrift;
          attribute float aOpacityRandom;
          attribute float aTwinkleStrength;
          attribute float aTwinkleSpeed;
          attribute float aTwinklePhase;
          uniform float uTime;
          uniform float uPointSize;
          uniform float uPointSizeDistanceScale;
          uniform float uMovementSpeed;
          uniform float uDriftStrength;
          uniform float uYWaveSpeed;
          uniform float uYDriftScale;
          uniform float uZWaveSpeed;
          uniform float uZDriftScale;
          uniform float uTwinkleEnabled;
          uniform float uTwinkleIntensity;
          uniform float uTwinkleSharpness;
          uniform float uMouseEnabled;
          uniform vec2 uMouseNdc;
          uniform vec2 uMouseTrailNdc;
          uniform float uMouseInfluence;
          uniform float uMouseTrailInfluence;
          uniform float uMouseRadius;
          uniform float uMouseSoftness;
          uniform float uMouseFlowStrength;
          uniform float uMouseSwirlStrength;
          uniform float uMouseBreathStrength;
          uniform float uMouseTrailRadiusMultiplier;
          uniform float uMouseTrailFlowMultiplier;
          uniform float uMouseDepthScale;
          uniform float uMouseZDrift;
          uniform float uMouseNoiseStrength;
          uniform float uMousePulseSpeed;
          varying float vOpacityMultiplier;
          varying float vTwinkleGlow;
          varying float vMouseGlow;

          void main() {
            vec3 pos = position;
            float t = uTime * uMovementSpeed * aSpeed + aPhase;
            pos.x += sin(t) * uDriftStrength * aDrift;
            pos.y += cos(t * uYWaveSpeed) * uDriftStrength * uYDriftScale * aDrift;
            pos.z += sin(t * uZWaveSpeed) * uDriftStrength * uZDriftScale * aDrift;

            float twinkleWave = sin(uTime * aTwinkleSpeed + aTwinklePhase) * 0.5 + 0.5;
            float twinkle = pow(twinkleWave, uTwinkleSharpness) * aTwinkleStrength * uTwinkleEnabled;
            vOpacityMultiplier = aOpacityRandom * (1.0 + twinkle * uTwinkleIntensity);
            vTwinkleGlow = twinkle * uTwinkleIntensity;

            vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
            vec4 baseClipPosition = projectionMatrix * mvPosition;
            vec2 particleNdc = baseClipPosition.xy / max(0.0001, baseClipPosition.w);

            float softEdge = max(0.0001, uMouseRadius * max(0.05, uMouseSoftness));
            vec2 fromMouse = particleNdc - uMouseNdc;
            float distanceFromMouse = length(fromMouse);
            float mouseFalloff = exp(-pow(distanceFromMouse / softEdge, 2.0)) * uMouseInfluence * uMouseEnabled;

            float trailRadius = uMouseRadius * uMouseTrailRadiusMultiplier;
            float trailSoftEdge = max(0.0001, trailRadius * max(0.05, uMouseSoftness));
            vec2 fromTrail = particleNdc - uMouseTrailNdc;
            float distanceFromTrail = length(fromTrail);
            float trailFalloff = exp(-pow(distanceFromTrail / trailSoftEdge, 2.0)) * uMouseTrailInfluence * uMouseEnabled;

            vec2 mouseDir = distanceFromMouse > 0.0001
              ? fromMouse / distanceFromMouse
              : vec2(cos(aPhase), sin(aPhase));
            vec2 mouseTangent = vec2(-mouseDir.y, mouseDir.x);
            vec2 trailDir = distanceFromTrail > 0.0001
              ? fromTrail / distanceFromTrail
              : vec2(cos(aPhase + 1.7), sin(aPhase + 1.7));
            vec2 trailTangent = vec2(-trailDir.y, trailDir.x);

            float pulse = sin(uTime * uMousePulseSpeed + aPhase * 2.0) * 0.5 + 0.5;
            float organicNoise = sin(uTime * 0.8 + aPhase * 4.0) * uMouseNoiseStrength;
            vec2 softMouseFlow = mouseTangent * uMouseSwirlStrength
              + mouseDir * uMouseBreathStrength * (pulse - 0.5 + organicNoise);
            vec2 softTrailFlow = trailTangent * uMouseSwirlStrength * uMouseTrailFlowMultiplier
              + trailDir * uMouseBreathStrength * 0.45 * (pulse - 0.5);

            float depthScale = max(1.0, -mvPosition.z) * uMouseDepthScale;
            vec2 totalSoftFlow = softMouseFlow * mouseFalloff * uMouseFlowStrength
              + softTrailFlow * trailFalloff * uMouseFlowStrength;
            float totalField = max(mouseFalloff, trailFalloff * uMouseTrailFlowMultiplier);
            mvPosition.xy += totalSoftFlow * depthScale;
            mvPosition.z += sin(uTime * uMousePulseSpeed + aPhase) * totalField * uMouseZDrift * depthScale;
            vMouseGlow = totalField * 0.62;

            gl_PointSize = uPointSize * (uPointSizeDistanceScale / max(1.0, -mvPosition.z));
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          uniform vec3 uColor;
          uniform float uOpacity;
          uniform float uGlowIntensity;
          uniform float uAlphaEdgeStart;
          uniform float uAlphaEdgeEnd;
          uniform float uAlphaDiscard;
          varying float vOpacityMultiplier;
          varying float vTwinkleGlow;
          varying float vMouseGlow;

          void main() {
            vec2 point = gl_PointCoord - vec2(0.5);
            float dist = length(point);
            float innerEdge = min(uAlphaEdgeStart, uAlphaEdgeEnd);
            float outerEdge = max(uAlphaEdgeStart, uAlphaEdgeEnd);
            float core = 1.0 - smoothstep(innerEdge, outerEdge, dist);
            float softGlow = 1.0 - smoothstep(0.08, 0.5, dist);
            float alpha = clamp(core * uOpacity * vOpacityMultiplier + softGlow * uOpacity * 0.22 * (vTwinkleGlow + vMouseGlow), 0.0, 1.0);
            if (alpha < uAlphaDiscard) discard;
            vec3 glowColor = uColor * (1.0 + vTwinkleGlow * uGlowIntensity + vMouseGlow * uGlowIntensity);
            gl_FragColor = vec4(glowColor, alpha);
          }
        `
      });

      const particles = new THREE.Points(geometry, material);
      particles.frustumCulled = false;
      particles.renderOrder = INNER_PARTICLES_CONFIG.renderOrder;
      return particles;
    };

    let environmentTexture = null;
    const textureLoader = new THREE.TextureLoader(loadingManager);
    textureLoader.load(HDR_TEXTURE_PATH, (texture) => {
      if (!isMounted) {
        texture.dispose();
        return;
      }

      texture.colorSpace = THREE.SRGBColorSpace;
      texture.mapping = THREE.EquirectangularReflectionMapping;
      environmentTexture = texture;

      scene.environment = texture;
      scene.environmentIntensity = NIGHT_ENVIRONMENT_CONFIG.environmentIntensity;
    });

    createPointLightWithGuide(POINT_LIGHT_1_CONFIG, 'viewModelPage Point Light 1');
    createPointLightWithGuide(POINT_LIGHT_2_CONFIG, 'viewModelPage Point Light 2');
    createPointLightWithGuide(POINT_LIGHT_2_1_CONFIG, 'viewModelPage Point Light 2 1');
    createPointLightWithGuide(POINT_LIGHT_2_2_CONFIG, 'viewModelPage Point Light 2 2');
    createPointLightWithGuide(POINT_LIGHT_2_3_CONFIG, 'viewModelPage Point Light 2 3');
    createPointLightWithGuide(POINT_LIGHT_2_4_CONFIG, 'viewModelPage Point Light 2 4');
    createPointLightWithGuide(POINT_LIGHT_3_CONFIG, 'viewModelPage Point Light 3');
    createPointLightWithGuide(POINT_LIGHT_4_CONFIG, 'viewModelPage Point Light 4');

    const backgroundParticles = createBackgroundParticles();
    if (backgroundParticles) {
      if (BACKGROUND_PARTICLES_CONFIG.followCameraPosition) {
        backgroundParticles.position.copy(camera.position);
      }
      scene.add(backgroundParticles);
    }

    const innerSphereParticles = createInnerSphereParticles();
    if (innerSphereParticles) {
      if (INNER_PARTICLES_CONFIG.followCameraPosition) {
        innerSphereParticles.position.copy(camera.position);
      }
      scene.add(innerSphereParticles);
    }

    const registerSceneRevealMaterial = (material, finalOpacity = 1, forceTransparent = true) => {
      material.userData.finalOpacity = finalOpacity;
      material.userData.forceTransparent = forceTransparent;
      sceneRevealMaterials.push(material);
      return material;
    };

    const createSceneTextGlowTexture = () => {
      const size = Math.max(32, Math.floor(SCENE_TEXT_HOVER_CONFIG.glowTextureSize));
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;

      const context = canvas.getContext('2d');
      const center = size * 0.5;
      const gradient = context.createRadialGradient(center, center, 0, center, center, center);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
      gradient.addColorStop(0.28, 'rgba(255, 255, 255, 0.62)');
      gradient.addColorStop(0.62, 'rgba(255, 255, 255, 0.18)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

      context.fillStyle = gradient;
      context.fillRect(0, 0, size, size);

      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.needsUpdate = true;
      return texture;
    };

    const sceneTextGlowTexture = SCENE_TEXT_HOVER_CONFIG.glowEnabled
      ? createSceneTextGlowTexture()
      : null;

    const createSceneTextMaterial = () => {
      const material = new THREE.MeshStandardMaterial({
        color: new THREE.Color(SCENE_TEXT_CONFIG.color),
        emissive: new THREE.Color(SCENE_TEXT_CONFIG.emissiveColor),
        emissiveIntensity: SCENE_TEXT_HOVER_CONFIG.enabled
          ? SCENE_TEXT_HOVER_CONFIG.inactiveEmissiveIntensity
          : SCENE_TEXT_CONFIG.emissiveIntensity,
        metalness: SCENE_TEXT_CONFIG.metalness,
        roughness: SCENE_TEXT_CONFIG.roughness,
        transparent: true,
        opacity: SCENE_TEXT_CONFIG.opacity * sceneTextOpacity,
        depthWrite: false
      });

      registerSceneRevealMaterial(material, SCENE_TEXT_CONFIG.opacity, true);
      return material;
    };

    const buildCenteredTextGeometry = (font, text, options) => {
      const geometry = new TextGeometry(text, {
        font,
        size: options.size,
        depth: options.depth,
        height: options.depth,
        curveSegments: options.curveSegments,
        bevelEnabled: options.bevelEnabled,
        bevelThickness: options.bevelThickness,
        bevelSize: options.bevelSize,
        bevelSegments: options.bevelSegments
      });
      geometry.computeBoundingBox();

      const box = geometry.boundingBox;
      const width = box.max.x - box.min.x;
      const height = box.max.y - box.min.y;
      const depth = box.max.z - box.min.z;
      geometry.translate(
        -box.min.x - width * 0.5,
        -box.min.y - height * 0.5,
        -box.min.z - depth * 0.5
      );
      geometry.computeBoundingBox();
      geometry.computeVertexNormals();

      return { geometry, width, height, depth };
    };

    const buildBaselineTextGeometry = (font, text, options) => {
      const geometry = new TextGeometry(text, {
        font,
        size: options.size,
        depth: options.depth,
        height: options.depth,
        curveSegments: options.curveSegments,
        bevelEnabled: options.bevelEnabled,
        bevelThickness: options.bevelThickness,
        bevelSize: options.bevelSize,
        bevelSegments: options.bevelSegments
      });
      geometry.computeBoundingBox();

      const box = geometry.boundingBox;
      const width = box.max.x - box.min.x;
      const height = box.max.y - box.min.y;
      const depth = box.max.z - box.min.z;

      // Keep Y on the font baseline. This prevents lowercase letters, hyphens,
      // and small subtitle glyphs from becoming crooked or vertically re-centered one by one.
      geometry.translate(
        -box.min.x,
        0,
        -box.min.z - depth * 0.5
      );
      geometry.computeBoundingBox();
      geometry.computeVertexNormals();

      const fixedBox = geometry.boundingBox;
      return {
        geometry,
        width,
        height,
        depth,
        minY: fixedBox.min.y,
        maxY: fixedBox.max.y,
        centerY: (fixedBox.min.y + fixedBox.max.y) * 0.5
      };
    };

    const createTextLetterMesh = (font, letter, size) => {
      const textGeometry = buildBaselineTextGeometry(font, letter, {
        size,
        depth: SCENE_TEXT_CONFIG.depth,
        curveSegments: SCENE_TEXT_CONFIG.curveSegments,
        bevelEnabled: SCENE_TEXT_CONFIG.bevelEnabled,
        bevelThickness: SCENE_TEXT_CONFIG.bevelThickness,
        bevelSize: SCENE_TEXT_CONFIG.bevelSize,
        bevelSegments: SCENE_TEXT_CONFIG.bevelSegments
      });

      const material = createSceneTextMaterial();
      const mesh = new THREE.Mesh(textGeometry.geometry, material);
      mesh.castShadow = SCENE_TEXT_CONFIG.castShadow;
      mesh.receiveShadow = SCENE_TEXT_CONFIG.receiveShadow;
      mesh.userData.isSceneTextHoverSurface = true;
      mesh.userData.hoverStrength = SCENE_TEXT_HOVER_CONFIG.enabled ? 0 : 1;
      mesh.userData.hoverTarget = SCENE_TEXT_HOVER_CONFIG.enabled ? 0 : 1;
      mesh.userData.hoverMaterial = material;

      if (SCENE_TEXT_HOVER_CONFIG.glowEnabled && sceneTextGlowTexture) {
        const glowMaterial = new THREE.SpriteMaterial({
          map: sceneTextGlowTexture,
          color: new THREE.Color(SCENE_TEXT_HOVER_CONFIG.glowColor),
          transparent: true,
          opacity: 0,
          depthWrite: false,
          depthTest: SCENE_TEXT_HOVER_CONFIG.glowDepthTest,
          blending: THREE.AdditiveBlending
        });

        const glowSprite = new THREE.Sprite(glowMaterial);
        const glowWidth = Math.max(
          SCENE_TEXT_HOVER_CONFIG.glowMinWidth,
          textGeometry.width * SCENE_TEXT_HOVER_CONFIG.glowWidthMultiplier
        );
        const glowHeight = Math.max(
          SCENE_TEXT_HOVER_CONFIG.glowMinHeight,
          textGeometry.height * SCENE_TEXT_HOVER_CONFIG.glowHeightMultiplier
        );

        glowSprite.name = `Glow - ${letter}`;
        glowSprite.scale.set(glowWidth, glowHeight, 1);
        glowSprite.position.set(
          textGeometry.width * 0.5,
          textGeometry.centerY,
          textGeometry.depth * 0.5 + SCENE_TEXT_HOVER_CONFIG.glowZOffset
        );
        glowSprite.renderOrder = SCENE_TEXT_HOVER_CONFIG.glowRenderOrder;
        glowSprite.visible = false;

        mesh.add(glowSprite);
        mesh.userData.hoverGlowMaterial = glowMaterial;
        mesh.userData.hoverGlowSprite = glowSprite;
      }

      sceneTextHoverMeshes.push(mesh);
      sceneTextHoverLetters.push(mesh);

      return {
        mesh,
        width: textGeometry.width,
        height: textGeometry.height,
        minY: textGeometry.minY,
        maxY: textGeometry.maxY
      };
    };

    const buildLetterTextLine = (font, text, size) => {
      const group = new THREE.Group();
      group.name = `Letter Line - ${text}`;

      const spaceWidth = size * SCENE_TEXT_CONFIG.spaceWidthRatio;
      const letterSpacing = SCENE_TEXT_CONFIG.letterSpacing;
      let cursorX = 0;
      let minY = Infinity;
      let maxY = -Infinity;
      let hasLetters = false;

      [...text].forEach((letter) => {
        if (letter === ' ') {
          cursorX += spaceWidth + letterSpacing;
          return;
        }

        const letterData = createTextLetterMesh(font, letter, size);
        letterData.mesh.position.x = cursorX;
        letterData.mesh.position.y = 0;
        letterData.mesh.position.z = 0;
        group.add(letterData.mesh);

        cursorX += letterData.width + letterSpacing;
        minY = Math.min(minY, letterData.minY);
        maxY = Math.max(maxY, letterData.maxY);
        hasLetters = true;
      });

      const width = Math.max(0, cursorX - letterSpacing);
      const lineMinY = hasLetters ? minY : 0;
      const lineMaxY = hasLetters ? maxY : 0;
      const height = lineMaxY - lineMinY;

      // Center only the actual letter layout on X. Do not use Box3 here,
      // because glow sprites are children and must never change text spacing/layout.
      group.children.forEach((child) => {
        child.position.x -= width * 0.5;
      });

      return {
        group,
        width,
        height,
        minY: lineMinY,
        maxY: lineMaxY
      };
    };

    const createSceneText = (font) => {
      if (!SCENE_TEXT_CONFIG.enabled) return null;

      const group = new THREE.Group();
      group.name = 'Scene Text - ICEBERG INVESTMENTS';

      const title = buildLetterTextLine(font, SCENE_TEXT_CONFIG.title, SCENE_TEXT_CONFIG.titleSize);
      const subtitle = buildLetterTextLine(font, SCENE_TEXT_CONFIG.subtitle, SCENE_TEXT_CONFIG.subtitleSize);

      const maxSubtitleWidth = title.width * SCENE_TEXT_CONFIG.subtitleFitWidthRatio;
      const subtitleFitScale = subtitle.width > 0 ? Math.min(1, maxSubtitleWidth / subtitle.width) : 1;
      subtitle.group.scale.setScalar(subtitleFitScale);

      // Place subtitle by the real glyph bounds only. Glow size is intentionally ignored.
      title.group.position.y = 0;
      subtitle.group.position.y = title.minY - SCENE_TEXT_CONFIG.lineGap - subtitle.maxY * subtitleFitScale;

      const totalMinY = Math.min(
        title.group.position.y + title.minY,
        subtitle.group.position.y + subtitle.minY * subtitleFitScale
      );
      const totalMaxY = Math.max(
        title.group.position.y + title.maxY,
        subtitle.group.position.y + subtitle.maxY * subtitleFitScale
      );
      const totalCenterY = (totalMinY + totalMaxY) * 0.5;

      title.group.position.y -= totalCenterY;
      subtitle.group.position.y -= totalCenterY;

      group.add(title.group);
      group.add(subtitle.group);

      group.position.copy(SCENE_TEXT_CONFIG.position);
      group.rotation.set(
        THREE.MathUtils.degToRad(SCENE_TEXT_CONFIG.rotationXDeg),
        THREE.MathUtils.degToRad(SCENE_TEXT_CONFIG.rotationYDeg),
        THREE.MathUtils.degToRad(SCENE_TEXT_CONFIG.rotationZDeg)
      );
      group.scale.setScalar(SCENE_TEXT_CONFIG.scale);
      group.visible = !SCENE_TEXT_CONFIG.visibleAfterFirstScroll || sceneTextTargetOpacity > 0;
      setSceneTextOpacity(sceneTextOpacity);

      scene.add(group);
      return group;
    };

    const createRoundedRectangleShape = (width, height, radius) => {
      const halfWidth = width * 0.5;
      const halfHeight = height * 0.5;
      const safeRadius = Math.max(0, Math.min(radius, halfWidth, halfHeight));
      const shape = new THREE.Shape();

      shape.moveTo(-halfWidth + safeRadius, -halfHeight);
      shape.lineTo(halfWidth - safeRadius, -halfHeight);
      shape.quadraticCurveTo(halfWidth, -halfHeight, halfWidth, -halfHeight + safeRadius);
      shape.lineTo(halfWidth, halfHeight - safeRadius);
      shape.quadraticCurveTo(halfWidth, halfHeight, halfWidth - safeRadius, halfHeight);
      shape.lineTo(-halfWidth + safeRadius, halfHeight);
      shape.quadraticCurveTo(-halfWidth, halfHeight, -halfWidth, halfHeight - safeRadius);
      shape.lineTo(-halfWidth, -halfHeight + safeRadius);
      shape.quadraticCurveTo(-halfWidth, -halfHeight, -halfWidth + safeRadius, -halfHeight);

      return shape;
    };

    const createSceneButton = (font) => {
      if (!SCENE_BUTTON_CONFIG.enabled) return null;

      const group = new THREE.Group();
      group.name = 'Scene 3D Button';

      const buttonShape = createRoundedRectangleShape(
        SCENE_BUTTON_CONFIG.width,
        SCENE_BUTTON_CONFIG.height,
        SCENE_BUTTON_CONFIG.radius
      );
      const buttonGeometry = new THREE.ExtrudeGeometry(buttonShape, {
        depth: SCENE_BUTTON_CONFIG.depth,
        curveSegments: SCENE_BUTTON_CONFIG.curveSegments,
        bevelEnabled: SCENE_BUTTON_CONFIG.bevelEnabled,
        bevelThickness: SCENE_BUTTON_CONFIG.bevelThickness,
        bevelSize: SCENE_BUTTON_CONFIG.bevelSize,
        bevelSegments: SCENE_BUTTON_CONFIG.bevelSegments
      });
      buttonGeometry.translate(0, 0, -SCENE_BUTTON_CONFIG.depth * 0.5);
      buttonGeometry.computeVertexNormals();

      const buttonMaterial = new THREE.MeshStandardMaterial({
        color: new THREE.Color(SCENE_BUTTON_CONFIG.backgroundColor),
        emissive: new THREE.Color(SCENE_BUTTON_CONFIG.backgroundEmissiveColor),
        emissiveIntensity: SCENE_BUTTON_CONFIG.backgroundEmissiveIntensity,
        metalness: SCENE_BUTTON_CONFIG.metalness,
        roughness: SCENE_BUTTON_CONFIG.roughness,
        transparent: true,
        opacity: SCENE_BUTTON_CONFIG.backgroundOpacity * sceneTextOpacity,
        depthWrite: false
      });
      registerSceneRevealMaterial(buttonMaterial, SCENE_BUTTON_CONFIG.backgroundOpacity, true);

      const buttonMesh = new THREE.Mesh(buttonGeometry, buttonMaterial);
      buttonMesh.castShadow = SCENE_BUTTON_CONFIG.castShadow;
      buttonMesh.receiveShadow = SCENE_BUTTON_CONFIG.receiveShadow;
      buttonMesh.userData.isSceneButtonHoverSurface = true;
      group.add(buttonMesh);

      const borderGeometry = new THREE.EdgesGeometry(buttonGeometry, 18);
      const borderMaterial = new THREE.LineBasicMaterial({
        color: new THREE.Color(SCENE_BUTTON_CONFIG.borderColor),
        transparent: true,
        opacity: SCENE_BUTTON_CONFIG.borderOpacity * sceneTextOpacity,
        depthWrite: false
      });
      registerSceneRevealMaterial(borderMaterial, SCENE_BUTTON_CONFIG.borderOpacity, true);
      const borderLines = new THREE.LineSegments(borderGeometry, borderMaterial);
      group.add(borderLines);

      const labelGeometryData = buildCenteredTextGeometry(font, SCENE_BUTTON_CONFIG.label, {
        size: SCENE_BUTTON_CONFIG.labelSize,
        depth: SCENE_BUTTON_CONFIG.labelDepth,
        curveSegments: 10,
        bevelEnabled: false,
        bevelThickness: 0,
        bevelSize: 0,
        bevelSegments: 0
      });
      const labelMaterial = new THREE.MeshStandardMaterial({
        color: new THREE.Color(SCENE_BUTTON_CONFIG.labelColor),
        emissive: new THREE.Color(SCENE_BUTTON_CONFIG.labelEmissiveColor),
        emissiveIntensity: SCENE_BUTTON_CONFIG.labelEmissiveIntensity,
        metalness: SCENE_BUTTON_CONFIG.metalness,
        roughness: SCENE_BUTTON_CONFIG.roughness,
        transparent: true,
        opacity: sceneTextOpacity,
        depthWrite: false
      });
      registerSceneRevealMaterial(labelMaterial, 1, true);

      const labelMesh = new THREE.Mesh(labelGeometryData.geometry, labelMaterial);
      const maxLabelWidth = SCENE_BUTTON_CONFIG.width * SCENE_BUTTON_CONFIG.labelFitWidthRatio;
      const labelScale = labelGeometryData.width > 0 ? Math.min(1, maxLabelWidth / labelGeometryData.width) : 1;
      labelMesh.scale.setScalar(labelScale);
      labelMesh.position.y = SCENE_BUTTON_CONFIG.labelYOffset;
      labelMesh.position.z = SCENE_BUTTON_CONFIG.depth * 0.5 + SCENE_BUTTON_CONFIG.labelZOffset;
      labelMesh.userData.isSceneButtonHoverSurface = true;
      group.add(labelMesh);

      const buttonHoverItem = {
        strength: 0,
        target: 0,
        buttonMaterial,
        labelMaterial,
        borderMaterial,
        glowMaterial: null,
        glowSprite: null
      };

      if (SCENE_BUTTON_CONFIG.glowEnabled && sceneTextGlowTexture) {
        const buttonGlowMaterial = new THREE.SpriteMaterial({
          map: sceneTextGlowTexture,
          color: new THREE.Color(SCENE_BUTTON_CONFIG.glowColor),
          transparent: true,
          opacity: 0,
          depthWrite: false,
          depthTest: SCENE_BUTTON_CONFIG.glowDepthTest,
          blending: THREE.AdditiveBlending
        });

        const buttonGlowSprite = new THREE.Sprite(buttonGlowMaterial);
        buttonGlowSprite.name = 'Glow - Scene Button';
        buttonGlowSprite.scale.set(
          SCENE_BUTTON_CONFIG.width * SCENE_BUTTON_CONFIG.glowWidthMultiplier,
          SCENE_BUTTON_CONFIG.height * SCENE_BUTTON_CONFIG.glowHeightMultiplier,
          1
        );
        buttonGlowSprite.position.z = SCENE_BUTTON_CONFIG.depth * 0.5 + SCENE_BUTTON_CONFIG.glowZOffset;
        buttonGlowSprite.renderOrder = SCENE_BUTTON_CONFIG.glowRenderOrder;
        buttonGlowSprite.visible = false;
        group.add(buttonGlowSprite);

        buttonHoverItem.glowMaterial = buttonGlowMaterial;
        buttonHoverItem.glowSprite = buttonGlowSprite;
      }

      buttonMesh.userData.sceneButtonHoverItem = buttonHoverItem;
      labelMesh.userData.sceneButtonHoverItem = buttonHoverItem;
      sceneButtonHoverMeshes.push(buttonMesh, labelMesh);
      sceneButtonHoverItems.push(buttonHoverItem);

      group.position.copy(SCENE_BUTTON_CONFIG.position);
      group.rotation.set(
        THREE.MathUtils.degToRad(SCENE_BUTTON_CONFIG.rotationXDeg),
        THREE.MathUtils.degToRad(SCENE_BUTTON_CONFIG.rotationYDeg),
        THREE.MathUtils.degToRad(SCENE_BUTTON_CONFIG.rotationZDeg)
      );
      group.scale.setScalar(SCENE_BUTTON_CONFIG.scale);
      group.visible = !SCENE_TEXT_CONFIG.visibleAfterFirstScroll || sceneTextTargetOpacity > 0;
      setSceneTextOpacity(sceneTextOpacity);

      scene.add(group);
      return group;
    };

    const sceneFont = new FontLoader().parse(helvetikerRegular);
    sceneTextGroup = createSceneText(sceneFont);
    sceneButtonGroup = createSceneButton(sceneFont);

    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('/draco/');

    const loader = new GLTFLoader(loadingManager);
    loader.setDRACOLoader(dracoLoader);

    const prepareModelMaterials = (model, config = FLOOR_MODEL_CONFIG) => {
      model.traverse((object) => {
        if (!object.isMesh) return;
        object.castShadow = config.castShadow;
        object.receiveShadow = config.receiveShadow;
        if (object.material?.map) {
          object.material.map.colorSpace = THREE.SRGBColorSpace;
        }
      });
    };

    if (FLOOR_MODEL_CONFIG.enabled) loader.load(
      FLOOR_MODEL_CONFIG.path,
      (gltf) => {
        if (!isMounted) return;

        const floorModel = gltf.scene;
        floorModel.position.copy(FLOOR_MODEL_CONFIG.position);
        floorModel.scale.copy(FLOOR_MODEL_CONFIG.scale);
        floorModel.rotation.set(
          FLOOR_MODEL_CONFIG.rotation.x,
          FLOOR_MODEL_CONFIG.rotation.y,
          FLOOR_MODEL_CONFIG.rotation.z
        );
        prepareModelMaterials(floorModel, FLOOR_MODEL_CONFIG);

        if (FLOOR_MODEL_CONFIG.autoCenter) {
          floorModel.updateMatrixWorld(true);
          const box = new THREE.Box3().setFromObject(floorModel);
          const center = box.getCenter(new THREE.Vector3());
          floorModel.position.sub(center);
        }
        floorModel.updateMatrixWorld(true);

        scene.add(floorModel);
      },
      undefined,
      (loadError) => {
        if (!isMounted) return;
        console.error('Error loading viewModelPage floor model:', loadError);
        setError('Model load failed');
      }
    );

    if (LOGO_CONFIG.enabled) {
      loader.load(
        LOGO_CONFIG.path,
        (gltf) => {
          if (!isMounted) return;

          const logoModel = gltf.scene;
          logoModel.position.copy(LOGO_CONFIG.position);
          logoModel.scale.setScalar(LOGO_CONFIG.scale);
          logoModel.rotation.set(
            LOGO_CONFIG.rotation.x,
            LOGO_CONFIG.rotation.y,
            LOGO_CONFIG.rotation.z
          );

          logoModel.traverse((object) => {
            if (!object.isMesh) return;
            object.castShadow = LOGO_CONFIG.castShadow;
            object.receiveShadow = LOGO_CONFIG.receiveShadow;
            if (object.material?.map) {
              object.material.map.colorSpace = THREE.SRGBColorSpace;
            }
          });

          scene.add(logoModel);
          rotatingLogos.push(logoModel);
        },
        undefined,
        (loadError) => {
          console.error('Error loading viewModelPage logo model:', loadError);
        }
      );
    }

    const updateSceneTextHover = (delta) => {
      if (!SCENE_TEXT_HOVER_CONFIG.enabled || sceneTextHoverLetters.length === 0) return;

      let hoveredLetter = null;
      const canCheckLetters = sceneTextPointerInside
        && sceneTextGroup
        && sceneTextGroup.visible
        && sceneTextOpacity > 0.03;

      if (canCheckLetters) {
        sceneTextRaycaster.setFromCamera(sceneTextPointer, camera);
        const intersections = sceneTextRaycaster.intersectObjects(sceneTextHoverMeshes, false);
        hoveredLetter = intersections.length > 0 ? intersections[0].object : null;
      }

      sceneTextHoverLetters.forEach((letterMesh) => {
        letterMesh.userData.hoverTarget = letterMesh === hoveredLetter ? 1 : 0;
        const currentStrength = letterMesh.userData.hoverStrength ?? 0;
        const targetStrength = letterMesh.userData.hoverTarget;
        const fadeSeconds = targetStrength > currentStrength
          ? SCENE_TEXT_HOVER_CONFIG.emissiveFadeInSeconds
          : SCENE_TEXT_HOVER_CONFIG.emissiveFadeOutSeconds;
        const hoverLerp = Math.min(1, delta / Math.max(0.001, fadeSeconds));
        const nextStrength = THREE.MathUtils.lerp(currentStrength, targetStrength, hoverLerp);
        const material = letterMesh.userData.hoverMaterial;
        const glowMaterial = letterMesh.userData.hoverGlowMaterial;
        const glowSprite = letterMesh.userData.hoverGlowSprite;

        letterMesh.userData.hoverStrength = nextStrength;
        if (material) {
          material.emissive.set(SCENE_TEXT_CONFIG.emissiveColor);
          material.emissiveIntensity = THREE.MathUtils.lerp(
            SCENE_TEXT_HOVER_CONFIG.inactiveEmissiveIntensity,
            SCENE_TEXT_HOVER_CONFIG.activeEmissiveIntensity,
            nextStrength
          );
          material.needsUpdate = true;
        }

        if (glowMaterial) {
          const glowOpacity = SCENE_TEXT_HOVER_CONFIG.glowEnabled
            ? nextStrength * SCENE_TEXT_HOVER_CONFIG.glowOpacity * sceneTextOpacity
            : 0;
          glowMaterial.color.set(SCENE_TEXT_HOVER_CONFIG.glowColor);
          glowMaterial.opacity = glowOpacity;
          glowMaterial.needsUpdate = true;
          if (glowSprite) glowSprite.visible = glowOpacity > 0.001;
        }
      });

      return !!hoveredLetter;
    };

    const updateSceneButtonHover = (delta) => {
      if (!SCENE_BUTTON_CONFIG.enabled || !SCENE_BUTTON_CONFIG.hoverEnabled || sceneButtonHoverItems.length === 0) return false;

      let hoveredButtonItem = null;
      const canCheckButton = sceneTextPointerInside
        && sceneButtonGroup
        && sceneButtonGroup.visible
        && sceneTextOpacity > 0.03;

      if (canCheckButton) {
        sceneTextRaycaster.setFromCamera(sceneTextPointer, camera);
        const intersections = sceneTextRaycaster.intersectObjects(sceneButtonHoverMeshes, false);
        hoveredButtonItem = intersections.length > 0
          ? intersections[0].object.userData.sceneButtonHoverItem
          : null;
      }

      sceneButtonHoverItems.forEach((buttonItem) => {
        buttonItem.target = buttonItem === hoveredButtonItem ? 1 : 0;
        const currentStrength = buttonItem.strength ?? 0;
        const fadeSeconds = buttonItem.target > currentStrength
          ? SCENE_BUTTON_CONFIG.hoverFadeInSeconds
          : SCENE_BUTTON_CONFIG.hoverFadeOutSeconds;
        const hoverLerp = Math.min(1, delta / Math.max(0.001, fadeSeconds));
        const nextStrength = THREE.MathUtils.lerp(currentStrength, buttonItem.target, hoverLerp);
        buttonItem.strength = nextStrength;

        if (buttonItem.buttonMaterial) {
          buttonItem.buttonMaterial.emissive.set(SCENE_BUTTON_CONFIG.backgroundEmissiveColor);
          buttonItem.buttonMaterial.emissiveIntensity = THREE.MathUtils.lerp(
            SCENE_BUTTON_CONFIG.backgroundEmissiveIntensity,
            SCENE_BUTTON_CONFIG.hoverBackgroundEmissiveIntensity,
            nextStrength
          );
          buttonItem.buttonMaterial.needsUpdate = true;
        }

        if (buttonItem.labelMaterial) {
          buttonItem.labelMaterial.emissive.set(SCENE_BUTTON_CONFIG.labelEmissiveColor);
          buttonItem.labelMaterial.emissiveIntensity = THREE.MathUtils.lerp(
            SCENE_BUTTON_CONFIG.labelEmissiveIntensity,
            SCENE_BUTTON_CONFIG.hoverLabelEmissiveIntensity,
            nextStrength
          );
          buttonItem.labelMaterial.needsUpdate = true;
        }

        if (buttonItem.borderMaterial) {
          buttonItem.borderMaterial.opacity = THREE.MathUtils.lerp(
            SCENE_BUTTON_CONFIG.borderOpacity,
            SCENE_BUTTON_CONFIG.hoverBorderOpacity,
            nextStrength
          ) * sceneTextOpacity;
          buttonItem.borderMaterial.needsUpdate = true;
        }

        if (buttonItem.glowMaterial) {
          const glowOpacity = SCENE_BUTTON_CONFIG.glowEnabled
            ? nextStrength * SCENE_BUTTON_CONFIG.glowOpacity * sceneTextOpacity
            : 0;
          buttonItem.glowMaterial.color.set(SCENE_BUTTON_CONFIG.glowColor);
          buttonItem.glowMaterial.opacity = glowOpacity;
          buttonItem.glowMaterial.needsUpdate = true;
          if (buttonItem.glowSprite) buttonItem.glowSprite.visible = glowOpacity > 0.001;
        }
      });

      return !!hoveredButtonItem;
    };

    const handleResize = () => {
      if (!mount) return;
      const width = mount.clientWidth;
      const height = Math.max(1, mount.clientHeight);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    };

    const animate = () => {
      animationFrameId = window.requestAnimationFrame(animate);
      const delta = clock.getDelta();

      if (LOGO_CONFIG.rotationEnabled && rotatingLogos.length > 0) {
        const duration = Math.max(0.001, LOGO_CONFIG.rotationDurationSeconds);
        const direction = LOGO_CONFIG.rotationDirection >= 0 ? 1 : -1;
        const amount = (Math.PI * 2 * delta * direction) / duration;

        rotatingLogos.forEach((logoModel) => {
          if (LOGO_CONFIG.rotationAxis === 'x') {
            logoModel.rotation.x += amount;
          } else if (LOGO_CONFIG.rotationAxis === 'z') {
            logoModel.rotation.z += amount;
          } else {
            logoModel.rotation.y += amount;
          }
        });
      }

      controls.update();
      currentCameraDistance = THREE.MathUtils.lerp(
        currentCameraDistance,
        targetCameraDistance,
        CAMERA_CONTROL_CONFIG.zoomLerp
      );
      controls.enableRotate = targetZoomStep === 0 && Math.abs(currentCameraDistance - targetCameraDistance) < 0.05;

      liveCameraOffset.copy(camera.position).sub(CAMERA_CONFIG.target);
      liveCameraSpherical.setFromVector3(liveCameraOffset);

      if (targetZoomStep > 0) {
        liveCameraSpherical.theta = THREE.MathUtils.lerp(
          liveCameraSpherical.theta,
          baseSpherical.theta,
          CAMERA_CONTROL_CONFIG.zoomBackAngleLerp
        );
        liveCameraSpherical.phi = THREE.MathUtils.lerp(
          liveCameraSpherical.phi,
          baseSpherical.phi,
          CAMERA_CONTROL_CONFIG.zoomBackAngleLerp
        );
      }

      liveCameraSpherical.radius = currentCameraDistance;
      camera.position.copy(CAMERA_CONFIG.target).add(liveCameraOffset.setFromSpherical(liveCameraSpherical));
      camera.lookAt(CAMERA_CONFIG.target);

      if (backgroundParticles) {
        if (BACKGROUND_PARTICLES_CONFIG.followCameraPosition) {
          backgroundParticles.position.copy(camera.position);
        }
        const uniforms = backgroundParticles.material.uniforms;
        uniforms.uTime.value = clock.elapsedTime;
        uniforms.uColor.value.set(BACKGROUND_PARTICLES_CONFIG.color);
        uniforms.uOpacity.value = BACKGROUND_PARTICLES_CONFIG.opacity;
        uniforms.uPointSize.value = BACKGROUND_PARTICLES_CONFIG.pointSize;
        uniforms.uPointSizeDistanceScale.value = BACKGROUND_PARTICLES_CONFIG.pointSizeDistanceScale;
        uniforms.uMovementSpeed.value = BACKGROUND_PARTICLES_CONFIG.movementSpeed;
        uniforms.uDriftStrength.value = BACKGROUND_PARTICLES_CONFIG.driftStrength;
        uniforms.uYWaveSpeed.value = BACKGROUND_PARTICLES_CONFIG.yWaveSpeed;
        uniforms.uYDriftScale.value = BACKGROUND_PARTICLES_CONFIG.yDriftScale;
        uniforms.uZWaveSpeed.value = BACKGROUND_PARTICLES_CONFIG.zWaveSpeed;
        uniforms.uZDriftScale.value = BACKGROUND_PARTICLES_CONFIG.zDriftScale;
        uniforms.uAlphaEdgeStart.value = BACKGROUND_PARTICLES_CONFIG.alphaEdgeStart;
        uniforms.uAlphaEdgeEnd.value = BACKGROUND_PARTICLES_CONFIG.alphaEdgeEnd;
        uniforms.uAlphaDiscard.value = BACKGROUND_PARTICLES_CONFIG.alphaDiscard;
        uniforms.uTwinkleEnabled.value = BACKGROUND_PARTICLES_CONFIG.twinkleEnabled ? 1 : 0;
        uniforms.uTwinkleIntensity.value = BACKGROUND_PARTICLES_CONFIG.twinkleIntensity;
        uniforms.uTwinkleSharpness.value = BACKGROUND_PARTICLES_CONFIG.twinkleSharpness;
      }

      if (innerSphereParticles) {
        if (INNER_PARTICLES_CONFIG.followCameraPosition) {
          innerSphereParticles.position.copy(camera.position);
        }

        const mouseFollowLerp = 1 - Math.exp(-delta * INNER_PARTICLES_CONFIG.mouseFollowSpeed);
        const mouseTrailFollowLerp = 1 - Math.exp(-delta * INNER_PARTICLES_CONFIG.mouseTrailFollowSpeed);
        const mouseFadeSpeed = innerParticleMouseTargetStrength > innerParticleMouseStrength
          ? INNER_PARTICLES_CONFIG.mouseFadeInSpeed
          : INNER_PARTICLES_CONFIG.mouseFadeOutSpeed;
        innerParticleMouseCurrent.lerp(innerParticleMouseTarget, mouseFollowLerp);
        innerParticleMouseTrail.lerp(innerParticleMouseCurrent, mouseTrailFollowLerp);
        innerParticleMouseStrength = THREE.MathUtils.lerp(
          innerParticleMouseStrength,
          innerParticleMouseTargetStrength,
          1 - Math.exp(-delta * mouseFadeSpeed)
        );
        innerParticleMouseTrailStrength = THREE.MathUtils.lerp(
          innerParticleMouseTrailStrength,
          INNER_PARTICLES_CONFIG.mouseTrailEnabled ? innerParticleMouseStrength : 0,
          1 - Math.exp(-delta * INNER_PARTICLES_CONFIG.mouseFadeOutSpeed)
        );

        const uniforms = innerSphereParticles.material.uniforms;
        uniforms.uTime.value = clock.elapsedTime;
        uniforms.uColor.value.set(INNER_PARTICLES_CONFIG.color);
        uniforms.uOpacity.value = INNER_PARTICLES_CONFIG.opacity;
        uniforms.uPointSize.value = INNER_PARTICLES_CONFIG.pointSize;
        uniforms.uPointSizeDistanceScale.value = INNER_PARTICLES_CONFIG.pointSizeDistanceScale;
        uniforms.uGlowIntensity.value = INNER_PARTICLES_CONFIG.glowIntensity;
        uniforms.uMovementSpeed.value = INNER_PARTICLES_CONFIG.movementSpeed;
        uniforms.uDriftStrength.value = INNER_PARTICLES_CONFIG.driftStrength;
        uniforms.uYWaveSpeed.value = INNER_PARTICLES_CONFIG.yWaveSpeed;
        uniforms.uYDriftScale.value = INNER_PARTICLES_CONFIG.yDriftScale;
        uniforms.uZWaveSpeed.value = INNER_PARTICLES_CONFIG.zWaveSpeed;
        uniforms.uZDriftScale.value = INNER_PARTICLES_CONFIG.zDriftScale;
        uniforms.uAlphaEdgeStart.value = INNER_PARTICLES_CONFIG.alphaEdgeStart;
        uniforms.uAlphaEdgeEnd.value = INNER_PARTICLES_CONFIG.alphaEdgeEnd;
        uniforms.uAlphaDiscard.value = INNER_PARTICLES_CONFIG.alphaDiscard;
        uniforms.uTwinkleEnabled.value = INNER_PARTICLES_CONFIG.twinkleEnabled ? 1 : 0;
        uniforms.uTwinkleIntensity.value = INNER_PARTICLES_CONFIG.twinkleIntensity;
        uniforms.uTwinkleSharpness.value = INNER_PARTICLES_CONFIG.twinkleSharpness;
        uniforms.uMouseEnabled.value = INNER_PARTICLES_CONFIG.mouseEnabled ? 1 : 0;
        uniforms.uMouseNdc.value.copy(innerParticleMouseCurrent);
        uniforms.uMouseTrailNdc.value.copy(innerParticleMouseTrail);
        uniforms.uMouseInfluence.value = innerParticleMouseStrength;
        uniforms.uMouseTrailInfluence.value = innerParticleMouseTrailStrength * INNER_PARTICLES_CONFIG.mouseTrailInfluence;
        uniforms.uMouseRadius.value = INNER_PARTICLES_CONFIG.mouseRadius;
        uniforms.uMouseSoftness.value = INNER_PARTICLES_CONFIG.mouseSoftness;
        uniforms.uMouseFlowStrength.value = INNER_PARTICLES_CONFIG.mouseFlowStrength;
        uniforms.uMouseSwirlStrength.value = INNER_PARTICLES_CONFIG.mouseSwirlStrength;
        uniforms.uMouseBreathStrength.value = INNER_PARTICLES_CONFIG.mouseBreathStrength;
        uniforms.uMouseTrailRadiusMultiplier.value = INNER_PARTICLES_CONFIG.mouseTrailRadiusMultiplier;
        uniforms.uMouseTrailFlowMultiplier.value = INNER_PARTICLES_CONFIG.mouseTrailFlowMultiplier;
        uniforms.uMouseDepthScale.value = INNER_PARTICLES_CONFIG.mouseDepthScale;
        uniforms.uMouseZDrift.value = INNER_PARTICLES_CONFIG.mouseZDrift;
        uniforms.uMouseNoiseStrength.value = INNER_PARTICLES_CONFIG.mouseNoiseStrength;
        uniforms.uMousePulseSpeed.value = INNER_PARTICLES_CONFIG.mousePulseSpeed;
      }

      if (sceneTextGroup || sceneButtonGroup) {
        const fadeSeconds = Math.max(0.001, SCENE_TEXT_CONFIG.fadeSeconds);
        const fadeLerp = Math.min(1, delta / fadeSeconds);
        sceneTextOpacity = THREE.MathUtils.lerp(sceneTextOpacity, sceneTextTargetOpacity, fadeLerp);
        setSceneTextOpacity(sceneTextOpacity);
        if (sceneTextOpacity <= 0.001 && sceneTextTargetOpacity === 0) {
          sceneTextOpacity = 0;
          setSceneTextOpacity(sceneTextOpacity);
          if (sceneTextGroup) sceneTextGroup.visible = false;
          if (sceneButtonGroup) sceneButtonGroup.visible = false;
          if (revealZoomHintAfterSceneHide) {
            revealZoomHintAfterSceneHide = false;
            zoomHintDismissedRef.current = false;
            if (ZOOM_HINT_CONFIG.enabled) setZoomHintVisible(true);
          }
        }
      }

      const hoveredText = updateSceneTextHover(delta);
      const hoveredButton = updateSceneButtonHover(delta);
      if (SCENE_TEXT_HOVER_CONFIG.cursorPointerOnLetters || SCENE_BUTTON_CONFIG.cursorPointerOnButton) {
        renderer.domElement.style.cursor = hoveredText || hoveredButton ? 'pointer' : '';
      }

      renderer.render(scene, camera);
    };

    window.addEventListener('resize', handleResize);
    animate();

    return () => {
      isMounted = false;
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(pageLoaderTimeoutId);
      window.removeEventListener('resize', handleResize);
      renderer.domElement.removeEventListener('wheel', handleCameraWheel);
      renderer.domElement.removeEventListener('pointermove', handleScenePointerMove);
      renderer.domElement.removeEventListener('pointerleave', handleScenePointerLeave);
      renderer.domElement.removeEventListener('pointerdown', handleScenePointerDown);
      renderer.domElement.style.cursor = '';
      controls.dispose();
      dracoLoader.dispose();
      if (environmentTexture) environmentTexture.dispose();
      if (backgroundParticles) {
        if (backgroundParticles.parent) backgroundParticles.parent.remove(backgroundParticles);
        backgroundParticles.geometry.dispose();
        backgroundParticles.material.dispose();
      }
      if (innerSphereParticles) {
        if (innerSphereParticles.parent) innerSphereParticles.parent.remove(innerSphereParticles);
        innerSphereParticles.geometry.dispose();
        innerSphereParticles.material.dispose();
      }
      lightGuides.forEach((guide) => {
        guide.geometry.dispose();
        guide.material.dispose();
      });
      if (sceneTextGroup) {
        if (sceneTextGroup.parent) sceneTextGroup.parent.remove(sceneTextGroup);
        sceneTextGroup.traverse((object) => {
          if (object.geometry) object.geometry.dispose();
          if (object.material) object.material.dispose();
        });
      }
      if (sceneButtonGroup) {
        if (sceneButtonGroup.parent) sceneButtonGroup.parent.remove(sceneButtonGroup);
        sceneButtonGroup.traverse((object) => {
          if (object.geometry) object.geometry.dispose();
          if (object.material) object.material.dispose();
        });
      }
      if (sceneTextGlowTexture) sceneTextGlowTexture.dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <main className="viewModelPage">
      {PAPER_BACKGROUND_CONFIG.enabled && (
        <div className="viewModelPage__paper-bg" aria-hidden="true">
          {PAPER_BACKGROUND_CONFIG.activeEffect === 'mesh' && (
            <MeshGradient
              className="viewModelPage__paper-shader"
              colors={[
                PAPER_BACKGROUND_CONFIG.color1,
                PAPER_BACKGROUND_CONFIG.color2,
                PAPER_BACKGROUND_CONFIG.color3,
                PAPER_BACKGROUND_CONFIG.color4
              ]}
              speed={PAPER_BACKGROUND_CONFIG.speed}
              backgroundColor={PAPER_BACKGROUND_CONFIG.backgroundColor}
              style={{
                transform: `translate3d(${PAPER_BACKGROUND_CONFIG.x}px, ${PAPER_BACKGROUND_CONFIG.y}px, ${PAPER_BACKGROUND_CONFIG.z}px) scale(${PAPER_BACKGROUND_CONFIG.scale})`
              }}
            />
          )}
        </div>
      )}
      <div ref={mountRef} className="viewModelPage__canvas" />
      {ZOOM_HINT_CONFIG.enabled && (
        <StarZoomHint visible={zoomHintVisible} />
      )}
      {PAGE_LOADER_CONFIG.enabled && (
        <div
          className={`viewModelPage__loader ${loading ? 'viewModelPage__loader--visible' : 'viewModelPage__loader--hidden'}`}
          style={{ transitionDuration: `${PAGE_LOADER_CONFIG.fadeSeconds}s` }}
          aria-hidden={!loading}
          aria-label={error ? 'Page load failed' : 'Page loading'}
        >
          <div className="viewModelPage__loader-ring" />
        </div>
      )}
    </main>
  );
};

export default ViewModelPage;

//